import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "./ValueBetsPage.css";
import {
  buildValueBetMarketRowLabel,
  buildValueBetMarketFamilyOnlyRowLabel,
  buildCanonicalValueBetDisplayLabel,
  buildValueBetTeamResolvedSelectionLabel,
  buildValueBetSelectionRowLabel,
  canonicalValueBetMarketForTranslation,
  classifySportForAnalysisTab,
  formatAnalysisOddsDisplay,
  formatAnalysisTime,
  getAnalysisTranslations,
  getAnalysisStatusLabel,
  mapFixtureBetToLabel,
  translateLeagueDisplay,
  translateMarketDisplay,
  translateMarketDisplayWithPeriodSplit,
  translateSelectionDisplay,
  translateValueMarketFamilyKey,
} from "./analysisTranslate";
import {
  isSuspiciousEpochLikeDate,
  isSuspiciousEpochLikeMs,
  parseAnalysisInstant,
} from "./analysisTimeUtils";
import * as sharpValueAdapterNs from "./sharpValueAdapter";

// ─── Shared odds format sync ───────────────────────────────────
const ANALYSIS_ODDS_FORMAT_KEY = "analysis_options_odds_format";
const VALID_ODDS_FORMATS = ["hk", "decimal", "american"];

function readOddsFormatFromStorage() {
  try {
    const v = localStorage.getItem(ANALYSIS_ODDS_FORMAT_KEY);
    if (v && VALID_ODDS_FORMATS.includes(v.toLowerCase())) return v.toLowerCase();
  } catch { /* ignore */ }
  return "hk";
}

function useOddsFormat() {
  const [oddsFormat, setOddsFormat] = React.useState(readOddsFormatFromStorage);
  React.useEffect(() => {
    function sync() {
      const next = readOddsFormatFromStorage();
      setOddsFormat((prev) => (prev !== next ? next : prev));
    }
    // Cross-tab sync (storage event fires in other windows)
    window.addEventListener("storage", sync);
    // Same-window sync via custom event dispatched by settings page
    window.addEventListener("analysis_odds_format_changed", sync);
    // Fallback poll every 300ms — handles same-window SPA tab switching
    const iv = setInterval(sync, 300);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("analysis_odds_format_changed", sync);
      clearInterval(iv);
    };
  }, []);
  return oddsFormat;
}
// ───────────────────────────────────────────────────────────────

const sharpValueDisplayAdapter =
  (sharpValueAdapterNs && sharpValueAdapterNs.default) || sharpValueAdapterNs || {};

let cachedAnalysisApiBase = null;

async function detectAnalysisApiBase() {
  if (cachedAnalysisApiBase !== null) return cachedAnalysisApiBase;
  const candidates = ["http://localhost:3001", ""];
  for (const base of candidates) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(`${base}/api/_selftest`, { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) continue;
      const j = await res.json().catch(() => null);
      if (j && j.ok === true) {
        cachedAnalysisApiBase = base;
        return base;
      }
    } catch {
      /* next */
    }
  }
  cachedAnalysisApiBase = "";
  return "";
}

function httpBaseToAnalysisWsUrl(base) {
  try {
    const b = String(base ?? "").trim();
    if (!b) {
      if (typeof window === "undefined") return "ws://localhost:3001/api/analysis/ws";
      const p = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${p}//${window.location.host}/api/analysis/ws`;
    }
    const u = new URL(b, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const wsP = u.protocol === "https:" ? "wss:" : "ws:";
    return `${wsP}//${u.host}/api/analysis/ws`;
  } catch {
    return null;
  }
}

function rawValueBetKey(raw) {
  if (!raw || typeof raw !== "object") return "";
  return String(
    raw.id ?? raw.bet_id ?? raw.valueBetId ?? raw.value_bet_id ?? ""
  ).trim();
}

function formatValueEvPercentForDisplay(raw) {
  const n = Number(raw);
  if (!isFinite(n)) return "";
  const sign = n >= 0 ? "+" : "";
  const s = n.toFixed(2).replace(/\.?0+$/, "");
  return `${sign}${s}%`;
}

function normalizeValueKeyToken(v) {
  if (v === undefined || v === null) return "";
  return String(v).trim().toLowerCase().replace(/\s+/g, " ");
}

function valueSelectionTokenRaw(v) {
  return normalizeValueKeyToken(v).replace(/_+/g, " ");
}

function normalizeValueSemanticText(v) {
  if (v === undefined || v === null) return "";
  let s = String(v).trim().toLowerCase();
  if (!s) return "";
  try {
    s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  } catch {
    /* ignore normalize errors */
  }
  s = s.replace(/\s*@\s*/g, " vs ");
  s = s.replace(/\bversus\b/g, " vs ");
  s = s.replace(/\bv\.\b/g, " vs ");
  s = s.replace(/\s+v\s+/g, " vs ");
  s = s.replace(/[^a-z0-9\s]+/g, " ");
  s = s.replace(/\bvs\b/g, " vs ");
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

function getValueSemanticKickoffBucket(bet) {
  const rawKickoff =
    bet?.start_time ??
    bet?.startTime ??
    bet?.event_start_time ??
    bet?.eventStartTime ??
    bet?.kickoff ??
    bet?.kickoffDate;
  const parsed = parseAnalysisInstant(rawKickoff);
  if (!parsed) return "";
  const yyyy = String(parsed.getFullYear()).padStart(4, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  const hh = String(parsed.getHours()).padStart(2, "0");
  const mi = String(parsed.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function getValueSemanticDisplayEventToken(bet) {
  const sport = normalizeValueSemanticText(
    bet?.sport ?? bet?.sportKey ?? bet?.sportName ?? bet?.category ?? bet?.gameType
  );
  const league = normalizeValueSemanticText(bet?.league);
  const kickoffBucket = getValueSemanticKickoffBucket(bet);
  const home = normalizeValueSemanticText(bet?.home_team ?? bet?.homeTeam);
  const away = normalizeValueSemanticText(bet?.away_team ?? bet?.awayTeam);
  if (home && away) {
    const teamPair = [home, away].sort().join(" v ");
    return `teams:${sport}|${league}|${teamPair}|${kickoffBucket}`;
  }
  const matchToken = normalizeValueSemanticText(
    bet?.match ?? bet?.event_name ?? bet?.eventName ?? bet?.game
  );
  if (matchToken) return `match:${sport}|${league}|${matchToken}|${kickoffBucket}`;
  const idToken = normalizeValueKeyToken(
    bet?.event_id ??
      bet?.eventId ??
      bet?.game_id ??
      bet?.gameId ??
      bet?.external_event_id ??
      bet?.externalEventId ??
      bet?.fixture_id ??
      bet?.fixtureId
  );
  if (!idToken) return "";
  return `id:${idToken}`;
}

function getValueSemanticEventIdentityToken(bet) {
  const idToken = normalizeValueKeyToken(
    bet?.event_id ??
      bet?.eventId ??
      bet?.game_id ??
      bet?.gameId ??
      bet?.external_event_id ??
      bet?.externalEventId ??
      bet?.fixture_id ??
      bet?.fixtureId
  );
  const matchToken = normalizeValueKeyToken(
    bet?.match ?? bet?.event_name ?? bet?.eventName ?? bet?.game ?? ""
  );
  if (idToken && matchToken) return `${idToken}#${matchToken}`;
  return idToken || matchToken;
}

function getValueSemanticPeriodToken(bet) {
  const explicit = normalizeValueKeyToken(
    bet?.periodScope ??
      bet?.period_scope ??
      bet?.period ??
      bet?.market_period ??
      bet?.marketPeriod ??
      bet?.time_scope ??
      bet?.timeScope
  );
  if (explicit) return explicit;
  try {
    const inferred = extractValueBetPeriodSuffix(
      bet,
      String(bet?.display_market ?? bet?.displayMarket ?? bet?.market ?? ""),
      String(bet?.market_type ?? bet?.marketType ?? bet?.market ?? "")
    );
    return normalizeValueKeyToken(inferred);
  } catch {
    return "";
  }
}

function getValueSemanticMarketIdentityToken(bet) {
  const canonical = normalizeValueSemanticText(
    canonicalValueBetMarketForTranslation(
      String(bet?.market ?? ""),
      String(bet?.market_type ?? bet?.marketType ?? "")
    ) || ""
  );
  const marketType = normalizeValueSemanticText(bet?.market_type ?? bet?.marketType);
  const marketRaw = normalizeValueSemanticText(bet?.market);
  const base = canonical || marketType || marketRaw;
  if (!base) return "";
  if (
    base === "moneyline" ||
    base === "match winner" ||
    base === "winner" ||
    base === "keo thang"
  ) {
    return "moneyline";
  }
  if (
    base === "total" ||
    base === "total points" ||
    base === "over under" ||
    base === "tai xiu"
  ) {
    return "total";
  }
  if (
    base === "spread" ||
    base === "handicap" ||
    base === "point spread" ||
    base === "cuoc chap"
  ) {
    return "spread";
  }
  if (base.startsWith("team total")) return "team_total";
  return base.replace(/\s+/g, "_");
}

function getValueSemanticSelectionToken(bet) {
  const home = normalizeValueSemanticText(bet?.home_team ?? bet?.homeTeam);
  const away = normalizeValueSemanticText(bet?.away_team ?? bet?.awayTeam);
  const playerName = normalizeValueSemanticText(bet?.player_name ?? bet?.playerName);
  const selectionRaw = valueSelectionTokenRaw(
    bet?.selection ?? bet?.display_selection ?? bet?.displaySelection ?? bet?.pick ?? bet?.outcome
  );
  const selectionTypeRaw = valueSelectionTokenRaw(bet?.selection_type ?? bet?.selectionType);
  const candidate = normalizeValueSemanticText(selectionRaw || selectionTypeRaw);
  if ((candidate === "home" || selectionTypeRaw === "home") && home) return home;
  if ((candidate === "away" || selectionTypeRaw === "away") && away) return away;
  if (playerName && (candidate === "player" || !candidate)) return playerName;
  return candidate || playerName;
}

function getSemanticValueOpportunityDisplayKey(bet) {
  if (!bet || typeof bet !== "object") return "";
  const scope = isLiveValueBetRow(bet) ? "live" : "prematch";
  const displayEventToken = getValueSemanticDisplayEventToken(bet);
  const sport = normalizeValueKeyToken(bet?.sport ?? bet?.sportKey ?? bet?.sportName ?? bet?.category ?? bet?.gameType);
  const league = normalizeValueKeyToken(bet?.league);
  const marketIdentity = getValueSemanticMarketIdentityToken(bet);
  const periodScope = getValueSemanticPeriodToken(bet);
  const selectionIdentity = getValueSemanticSelectionToken(bet);
  const line = normalizeValueKeyToken(bet?.line ?? bet?.point ?? bet?.handicap ?? bet?.total);
  const playerName = normalizeValueKeyToken(bet?.player_name ?? bet?.playerName);
  const statCategory = normalizeValueKeyToken(bet?.stat_category ?? bet?.statCategory);
  const isPlayerProp = normalizeValueKeyToken(bet?.is_player_prop ?? bet?.isPlayerProp);
  const sportsbook = normalizeValueKeyToken(bet?.sportsbook ?? bet?.bookmaker ?? bet?.book);
  const parts = [
    scope,
    displayEventToken,
    sport,
    league,
    marketIdentity,
    periodScope,
    selectionIdentity,
    line,
    playerName,
    statCategory,
    isPlayerProp,
    sportsbook,
  ];
  return `sd:${parts.join("|")}`;
}

/**
 * Stable identity key used for React reconciliation, flash detection and
 * "still the same opportunity" semantics. Preference order matches the backend
 * registry: `valueDedupKey` first, then any id-like field, then a deterministic
 * composite from event/book/market/selection/line/player fields.
 */
function getStableValueBetKey(bet) {
  if (!bet || typeof bet !== "object") return "";
  const dk = String(
    bet.valueDedupKey ??
      bet.value_dedup_key ??
      bet.dedupKey ??
      bet.dedup_key ??
      ""
  ).trim();
  if (dk) return dk;
  const idLike = String(
    bet.id ?? bet.bet_id ?? bet.valueBetId ?? bet.value_bet_id ?? ""
  ).trim();
  if (idLike) return `id:${idLike}`;
  const norm = (v) => (v === undefined || v === null ? "" : String(v).trim().toLowerCase());
  const parts = [
    "live",
    norm(bet.event_id ?? bet.eventId ?? bet.game_id ?? bet.gameId ?? bet.external_event_id ?? bet.externalEventId ?? bet.fixture_id ?? bet.fixtureId),
    norm(bet.sportsbook ?? bet.bookmaker ?? bet.book),
    norm(bet.market_type ?? bet.marketType ?? bet.market),
    norm(bet.selection ?? bet.selection_type ?? bet.selectionType ?? bet.selection_id ?? bet.selectionId),
    norm(bet.line ?? bet.point ?? bet.handicap ?? bet.total),
    norm(bet.player_name ?? bet.playerName),
    norm(bet.stat_category ?? bet.statCategory),
    norm(bet.is_player_prop ?? bet.isPlayerProp),
  ];
  return `c:${parts.join("|")}`;
}

function getValueDedupeStatusRank(bet) {
  const status = String(bet?.offerVerification?.status || "").trim();
  if (status === "verified") return 0;
  if (status === "source_verified") return 1;
  if (status === "grace_retained") return 2;
  return 3;
}

function getValueRowFreshnessRank(row) {
  const ov = row?.offerVerification && typeof row.offerVerification === "object" ? row.offerVerification : {};
  const ageOrInf = (v) =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : Number.POSITIVE_INFINITY;
  const tsOrZero = (v) => {
    const ms = parseDetectedAtMs(v);
    return typeof ms === "number" && Number.isFinite(ms) ? ms : 0;
  };
  return {
    rawAgeSeconds: ageOrInf(ov.rawAgeSeconds),
    sourceAgeSeconds: ageOrInf(ov.sourceAgeSeconds),
    verifiedAtMs: tsOrZero(ov.verifiedAt),
    lastSeenAtMs: tsOrZero(ov.lastSeenAt),
    lastUpdatedAtMs: tsOrZero(ov.lastUpdatedAt),
    rowUpdatedMs: Math.max(
      tsOrZero(row?.updatedAt),
      tsOrZero(row?.detected_at),
      tsOrZero(row?.detectedAt),
      tsOrZero(row?.timestamp),
      tsOrZero(row?.ts)
    ),
  };
}

function compareValueRowFreshness(a, b) {
  const fa = getValueRowFreshnessRank(a);
  const fb = getValueRowFreshnessRank(b);
  if (fa.rawAgeSeconds !== fb.rawAgeSeconds) return fa.rawAgeSeconds < fb.rawAgeSeconds ? 1 : -1;
  if (fa.sourceAgeSeconds !== fb.sourceAgeSeconds)
    return fa.sourceAgeSeconds < fb.sourceAgeSeconds ? 1 : -1;
  if (fa.verifiedAtMs !== fb.verifiedAtMs) return fa.verifiedAtMs > fb.verifiedAtMs ? 1 : -1;
  if (fa.lastSeenAtMs !== fb.lastSeenAtMs) return fa.lastSeenAtMs > fb.lastSeenAtMs ? 1 : -1;
  if (fa.lastUpdatedAtMs !== fb.lastUpdatedAtMs) return fa.lastUpdatedAtMs > fb.lastUpdatedAtMs ? 1 : -1;
  if (fa.rowUpdatedMs !== fb.rowUpdatedMs) return fa.rowUpdatedMs > fb.rowUpdatedMs ? 1 : -1;
  return 0;
}

function pickBetterValueSemanticRow(a, b) {
  if (!a) return b;
  if (!b) return a;
  const freshCmp = compareValueRowFreshness(a, b);
  if (freshCmp !== 0) return freshCmp > 0 ? a : b;
  const rankA = getValueDedupeStatusRank(a);
  const rankB = getValueDedupeStatusRank(b);
  if (rankA !== rankB) return rankA < rankB ? a : b;
  const decA = valueBetOfferedOddsToDecimal(a);
  const decB = valueBetOfferedOddsToDecimal(b);
  const hasDecA = Number.isFinite(decA);
  const hasDecB = Number.isFinite(decB);
  if (hasDecA && hasDecB && decA !== decB) return decA > decB ? a : b;
  if (hasDecA !== hasDecB) return hasDecA ? a : b;
  const evA = typeof a?.ev === "number" && Number.isFinite(a.ev) ? a.ev : Number.NEGATIVE_INFINITY;
  const evB = typeof b?.ev === "number" && Number.isFinite(b.ev) ? b.ev : Number.NEGATIVE_INFINITY;
  if (evA !== evB) return evA > evB ? a : b;
  const idA = normalizeValueKeyToken(a?.id ?? a?.bet_id ?? a?.valueBetId ?? a?.value_bet_id);
  const idB = normalizeValueKeyToken(b?.id ?? b?.bet_id ?? b?.valueBetId ?? b?.value_bet_id);
  if (idA && idB && idA !== idB) return idA > idB ? a : b;
  return a;
}

function getValueDisplayIdentityKey(bet) {
  if (!bet || typeof bet !== "object") return "";
  const semantic = String(
    bet?.displaySemanticKey ||
      bet?.display_semantic_key ||
      getSemanticValueOpportunityDisplayKey(bet) ||
      ""
  ).trim();
  if (semantic) return semantic;
  return getStableValueBetKey(bet);
}

function getValueRenderIdentityKey(bet) {
  if (!bet || typeof bet !== "object") return "";
  return String(bet?.displaySemanticKey || getValueDisplayIdentityKey(bet) || getStableValueBetKey(bet) || "").trim();
}

function mergeValueRowsBySemanticOpportunity(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const groups = new Map();
  const order = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row || typeof row !== "object") continue;
    const semanticKey =
      getSemanticValueOpportunityDisplayKey(row) || getStableValueBetKey(row) || `__value_sem_${i}`;
    if (!groups.has(semanticKey)) {
      groups.set(semanticKey, []);
      order.push(semanticKey);
    }
    groups.get(semanticKey).push(row);
  }
  return order.map((semanticKey) => {
    const group = groups.get(semanticKey) || [];
    const chosen = group.reduce((best, cur) => pickBetterValueSemanticRow(best, cur), null) || group[0] || null;
    const duplicateBooks = Array.from(
      new Set(
        group
          .map((r) => String(r?.sportsbook ?? r?.bookmaker ?? r?.book ?? "").trim())
          .filter(Boolean)
      )
    );
    const duplicateIds = group
      .map((r) => String(r?.id ?? r?.bet_id ?? r?.valueBetId ?? r?.value_bet_id ?? "").trim())
      .filter(Boolean);
    return {
      ...chosen,
      displaySemanticKey: semanticKey,
      _valueDuplicateCount: group.length,
      _valueDuplicateBooks: duplicateBooks,
      _valueDuplicateIds: duplicateIds,
    };
  });
}

function mergeRawValueBets(liveList, earlyList) {
  const map = new Map();
  let anon = 0;
  for (const it of earlyList || []) {
    const k = rawValueBetKey(it) || `__vb_${anon++}`;
    map.set(k, it);
  }
  for (const it of liveList || []) {
    const k = rawValueBetKey(it) || `__vb_${anon++}`;
    map.set(k, it);
  }
  return Array.from(map.values());
}


function getOfferVerificationBadgeMeta(bet) {
  const ov = bet?.offerVerification;
  if (!ov || typeof ov !== "object") return null;
  const st = String(ov.status || "").trim();
  const rawAge = ov.rawAgeSeconds;
  const sourceAge = ov.sourceAgeSeconds;
  const rawAgeHint =
    typeof rawAge === "number" && Number.isFinite(rawAge) && rawAge >= 0
      ? ` Raw age ~${Math.round(rawAge)}s.`
      : "";
  const sourceAgeHint =
    typeof sourceAge === "number" && Number.isFinite(sourceAge) && sourceAge >= 0
      ? ` Source age ~${Math.round(sourceAge)}s.`
      : "";
  if (st === "verified") {
    return {
      label: "Raw Verified",
      className: "vb-verify-badge is-verified",
      title: `Offer odds verified against same-book raw odds${rawAgeHint}`.trim(),
    };
  }
  if (st === "source_verified") {
    return {
      label: "Source Verified",
      className: "vb-verify-badge is-source",
      title: `Published from a fresh official Sharp EV feed row; exact same-book raw odds were not confirmed.${sourceAgeHint}`.trim(),
    };
  }
  if (st === "grace_retained") {
    const prev = String(ov.previousStatus || ov.retainedFromStatus || "").trim();
    const prevHint = prev ? ` Previously: ${prev}.` : "";
    return {
      label: "Grace",
      className: "vb-verify-badge is-grace",
      title: `Temporarily retained after last verified match.${prevHint}${rawAgeHint}${sourceAgeHint}`.trim(),
    };
  }
  return null;
}

/**
 * Derive a "ready to bet" hint from the backend `betReadiness` field, falling
 * back to `offerVerification.status` when older payloads do not yet carry
 * `betReadiness`. Source-verified rows are deliberately weaker than raw.
 *
 * @param {object} bet
 * @param {object} [readinessCtx] preflight / blocked UI context from the card renderer
 */
function getBetReadinessMeta(bet, readinessCtx) {
  const ctx = readinessCtx && typeof readinessCtx === "object" ? readinessCtx : {};
  if (ctx.preflightLoading) {
    return {
      label: "Checking...",
      className: "vb-ready-pill is-grace-check",
      title: "Đang kiểm tra raw odds với nhà cái…",
    };
  }
  if (ctx.recentPreflightReady) {
    const state = getValueBetCommercialState(bet);
    if (state.isSourceStatus) {
      return {
        label: "Check book",
        className: "vb-ready-pill is-source-check",
        title: "Preflight OK — open sportsbook in check mode, then confirm before placing.",
      };
    }
    return {
      label: "Ready",
      className: `vb-ready-pill ${state.readinessClassName || "is-raw-ready"}`,
      title:
        state.rawFreshnessBand === "warn"
          ? "Preflight OK — raw age close to limit. Place quickly."
          : "Preflight OK — same book / market / line / odds confirmed.",
    };
  }

  const state = getValueBetCommercialState(bet);
  // Never show permanent "Ready" from cached raw_ready alone — only after a fresh preflight (above).
  if (state.rawReadyStrict) {
    return {
      label: "Raw / Recheck",
      className: "vb-ready-pill is-grace-check",
      title: "Raw verified on feed — tap Vào cược to open the sportsbook.",
    };
  }
  if (state.readinessLabel === "Check book") {
    return {
      label: "Check book",
      className: "vb-ready-pill is-source-check",
      title: "Source verified from Sharp EV feed. Confirm at the sportsbook before placing.",
    };
  }
  if (state.readinessLabel === "Recheck") {
    return {
      label: "Recheck",
      className: "vb-ready-pill is-grace-check",
      title: "Recheck at the sportsbook before placing.",
    };
  }
  return {
    label: "Not ready",
    className: "vb-ready-pill is-not-ready",
    title: "Not currently verified. Do not place blindly.",
  };
}

function getValueRowReadinessRank(bet) {
  const state = getValueBetCommercialState(bet);
  if (state.sortGroup != null) return state.sortGroup;
  return 99;
}

function isLiveValueBetRow(bet) {
  if (!bet || typeof bet !== "object") return false;
  if (bet.isLive === true) return true;
  if (bet.is_live === true) return true;
  if (String(bet.scope || "").trim().toLowerCase() === "live") return true;
  const st = String(bet.status ?? bet.matchStatus ?? bet.state ?? bet.phase ?? "").toLowerCase();
  if (
    st.includes("live") ||
    st.includes("inplay") ||
    st.includes("in-play") ||
    st.includes("playing")
  ) {
    return true;
  }
  return false;
}

function decimalFromAmericanOdds(americanOdds) {
  const a = Number(americanOdds);
  if (!Number.isFinite(a) || a === 0) return null;
  if (a >= 100) return 1 + a / 100;
  if (a <= -100) return 1 + 100 / Math.abs(a);
  return null;
}

function hkFromAmericanOdds(americanOdds) {
  const dec = decimalFromAmericanOdds(americanOdds);
  return dec != null && dec > 1 ? dec - 1 : null;
}

function getValueBetCommercialState(bet) {
  const ov = bet?.offerVerification;
  const status = ov && typeof ov === "object" ? String(ov.status || "").trim() : "";
  const explicitReadiness = String(bet?.betReadiness || "").trim();
  const lifecycleStatus = String(bet?.lifecycleStatus || "").trim().toLowerCase();
  const isRawOfferVerified = bet?.isRawOfferVerified === true;
  const rawAgeSec =
    ov && typeof ov.rawAgeSeconds === "number" && Number.isFinite(ov.rawAgeSeconds) && ov.rawAgeSeconds >= 0
      ? ov.rawAgeSeconds
      : null;

  // Live raw odds drift in seconds, so we hard-cut at 5s. Prematch lines tolerate
  // a wider window, matching backend preflight thresholds (5s live / 60s prematch).
  const isLiveRow = isLiveValueBetRow(bet);
  const rawFreshnessBand = (() => {
    if (rawAgeSec == null) return "unknown";
    if (isLiveRow) {
      if (rawAgeSec <= 5) return "ready";
      if (rawAgeSec <= 8) return "warn";
      return "stale";
    }
    if (rawAgeSec <= 60) return "ready";
    if (rawAgeSec <= 120) return "warn";
    return "stale";
  })();
  // For live rows, "warn" must NOT be treated as fresh — the user-facing rule is
  // "If rawAgeSeconds > 5 for live: do not show active 'Vào cược'; show 'Kiểm lại'".
  // For prematch rows we keep the legacy tolerant interpretation.
  const rawAgeFresh = isLiveRow
    ? rawFreshnessBand === "ready" || rawFreshnessBand === "unknown"
    : rawFreshnessBand === "ready" || rawFreshnessBand === "warn" || rawFreshnessBand === "unknown";

  const isRawVerifiedStatus = status === "verified";
  const isSourceStatus = status === "source_verified";
  const isGraceStatus = status === "grace_retained";
  const teamTotalSafety = getValueTeamTotalSideSafety(bet);
  const teamTotalSafetyRequired = teamTotalSafety.isTeamTotalLike;
  const teamTotalSideVerified = teamTotalSafety.sideTrusted;
  const isRawTeamTotalSafe = !teamTotalSafetyRequired || teamTotalSafety.isRawTeamTotalSafe;

  const rawReadyStrict =
    isRawVerifiedStatus &&
    explicitReadiness === "raw_ready" &&
    isRawOfferVerified &&
    lifecycleStatus === "active" &&
    rawAgeFresh &&
    isRawTeamTotalSafe;
  const rawReadyBlockedByTeamTotalSide =
    teamTotalSafetyRequired &&
    isRawVerifiedStatus &&
    explicitReadiness === "raw_ready" &&
    isRawOfferVerified &&
    lifecycleStatus === "active" &&
    rawAgeFresh &&
    !teamTotalSafety.isRawTeamTotalSafe;
  // Live raw_verified rows whose raw odds drifted past the 5s ceiling are
  // structurally still raw_ready but no longer safe for click-time betting.
  // We expose them as "Kiểm lại" affordances (open book in check mode) so
  // the user can confirm the price at the bookmaker UI directly.
  const staleLiveRawRecheck =
    isLiveRow &&
    isRawVerifiedStatus &&
    explicitReadiness === "raw_ready" &&
    isRawOfferVerified &&
    lifecycleStatus === "active" &&
    !rawAgeFresh;

  let readinessLabel = "Not ready";
  let readinessClassName = "is-not-ready";
  if (rawReadyStrict) {
    readinessLabel = "Ready";
    readinessClassName = rawFreshnessBand === "warn" ? "is-raw-ready-warning" : "is-raw-ready";
  } else if (rawReadyBlockedByTeamTotalSide) {
    readinessLabel = "Check book";
    readinessClassName = "is-source-check";
  } else if (isSourceStatus) {
    readinessLabel = "Check book";
    readinessClassName = "is-source-check";
  } else if (isGraceStatus || (isRawVerifiedStatus && !rawAgeFresh)) {
    // Live verified rows whose raw is older than the live freshness window (>5s)
    // and prematch rows past the wider window also fall through here. They were
    // raw-verified at some point but must be re-checked at the bookmaker now.
    readinessLabel = "Recheck";
    readinessClassName = "is-grace-check";
  } else if (explicitReadiness === "source_check_required") {
    readinessLabel = "Check book";
    readinessClassName = "is-source-check";
  } else if (explicitReadiness === "grace_check_required") {
    readinessLabel = "Recheck";
    readinessClassName = "is-grace-check";
  }

  const sortGroup = rawReadyStrict
    ? 0
    : rawReadyBlockedByTeamTotalSide || (isSourceStatus && explicitReadiness === "source_check_required")
      ? 1
      : isGraceStatus && explicitReadiness === "grace_check_required"
        ? 2
        : 3;

  return {
    status,
    lifecycleStatus,
    rawAgeSec,
    rawFreshnessBand,
    rawAgeFresh,
    isLiveRow,
    isRawVerifiedStatus,
    rawReadyStrict,
    isSourceStatus,
    isGraceStatus,
    readinessLabel,
    readinessClassName,
    sortGroup,
    teamTotalSafetyRequired,
    teamTotalSideVerified,
    isRawTeamTotalSafe,
    rawReadyBlockedByTeamTotalSide,
    safetyDowngradedToCheck: rawReadyBlockedByTeamTotalSide,
    staleLiveRawRecheck,
  };
}

function getValueRowUpdateMs(bet) {
  if (!bet || typeof bet !== "object") return 0;
  const candidates = [
    bet?.offerVerification?.lastVerifiedAt,
    bet?.offerVerification?.lastUpdatedAt,
    bet?.offerVerification?.lastSeenAt,
    bet?.lastUpdatedAt,
    bet?.last_updated_at,
    bet?.updated_ts,
    bet?.updatedTs,
    bet?.timestamp,
    bet?.ts,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (typeof v === "number" && Number.isFinite(v)) {
      const a = Math.abs(v);
      if (a >= 1e12) return v;
      if (a >= 1e9) return Math.round(v * 1000);
    }
    if (typeof v === "string" && v) {
      const ms = Date.parse(v);
      if (Number.isFinite(ms)) return ms;
    }
  }
  return 0;
}

function formatAgeSecondsShort(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 60) return `${Math.round(n)}s`;
  const m = Math.floor(n / 60);
  const s = Math.round(n - m * 60);
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mr = m - h * 60;
  return mr ? `${h}h ${mr}m` : `${h}h`;
}

function formatValueBookName(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";

  const k = s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (k === "saba") return "Saba";
  if (k === "fanatics") return "Fanatics";
  if (k === "novig") return "Novig";
  if (k === "betmgm") return "BetMGM";
  if (k === "pinnacle" || k === "pin") return "Pinnacle";
  if (k === "betfair") return "Betfair";
  if (k === "sbobet" || k === "sbo") return "SBOBET";
  if (k === "onexbet" || k === "1xbet" || k === "1xbetcom") return "1xBet";
  if (k === "bookmaker") return "Bookmaker";
  if (k === "marathon" || k === "marathonbet") return "Marathon";
  if (k === "circasports" || k === "circasportsbook") return "Circa Sports";
  if (k === "prophetx") return "ProphetX";
  if (k === "188bet" || k === "188") return "188Bet";
  return s;
}

function extractBookNameValue(raw) {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "string" || typeof raw === "number") return String(raw).trim();
  if (typeof raw !== "object") return "";
  return String(raw.name ?? raw.key ?? raw.title ?? raw.label ?? raw.id ?? "").trim();
}

/**
 * Resolve the OFFERED betting sportsbook (where the user actually places the bet).
 *
 * Strict priority — does NOT fall back to sharp_book / devig_book / referenceBook
 * / matchedSportsbook, because those identify the fair / no-vig / verification
 * source, not the offered betting book. Showing those as the betting book would
 * mislead the user about where to actually wager.
 */
function getOfferedValueBookName(bet) {
  if (!bet || typeof bet !== "object") return "";
  const candidates = [
    bet.sportsbook,
    bet.bookmaker,
    bet.book,
    bet.bookmaker_name,
    bet.bookmakerName,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = extractBookNameValue(candidates[i]);
    if (value) return formatValueBookName(value);
  }
  if (Array.isArray(bet.sportsbooks)) {
    for (let i = 0; i < bet.sportsbooks.length; i += 1) {
      const value = extractBookNameValue(bet.sportsbooks[i]);
      if (value) return formatValueBookName(value);
    }
  }
  return "";
}

/**
 * Resolve the TRUE / FAIR odds source (the SharpAPI-side reference book or
 * de-vig source). This must NEVER be presented as the betting bookmaker.
 *
 * Priority:
 *   1. bet.sharp_book
 *   2. bet.sharpBook
 *   3. bet.devig_book
 *   4. bet.devigBook
 *   5. bet.referenceBook
 *   6. bet.no_vig_source / bet.noVigSource
 *   7. "SharpAPI" if devig_method exists
 *   8. fallback "SharpAPI"
 */
function getTrueOddsSourceName(bet) {
  if (!bet || typeof bet !== "object") return "SharpAPI";
  const candidates = [
    bet.sharp_book,
    bet.sharpBook,
    bet.devig_book,
    bet.devigBook,
    bet.referenceBook,
    bet.no_vig_source,
    bet.noVigSource,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = extractBookNameValue(candidates[i]);
    if (value) return formatValueBookName(value);
  }
  const devigMethod = String(bet.devig_method ?? bet.devigMethod ?? "").trim();
  if (devigMethod) return "SharpAPI";
  return "SharpAPI";
}

function normalizeValueTeamTotalSide(raw) {
  const s = String(raw ?? "").trim().toUpperCase();
  if (s === "HOME" || s === "H" || s === "1") return "HOME";
  if (s === "AWAY" || s === "A" || s === "2") return "AWAY";
  return "";
}

function normalizeValueTeamTotalDirection(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  if (!s) return "";
  if (s === "over" || s === "o" || s === "tai" || /\bover\b/.test(s) || /\btai\b/.test(s)) return "OVER";
  if (s === "under" || s === "u" || s === "xiu" || /\bunder\b/.test(s) || /\bxiu\b/.test(s)) return "UNDER";
  return "";
}

function isValueTeamTotalLikeMarket(bet, marketLabel = "") {
  if (!bet || typeof bet !== "object") return false;
  const rawBlob = [
    bet.market_type,
    bet.marketType,
    bet.market,
    bet.display_market,
    bet.displayMarket,
    marketLabel,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const lower = rawBlob.toLowerCase();
  const key = lower.replace(/[^a-z0-9]+/g, "_");
  return (
    key.includes("team_total") ||
    key.includes("team_total_goals") ||
    key.includes("team_total_points") ||
    key.includes("1st_half_team_total_goals") ||
    key.includes("1st_half_team_total_points") ||
    key.includes("2nd_half_team_total_goals") ||
    key.includes("2nd_half_team_total_points") ||
    /đội\s*đơn/i.test(rawBlob) ||
    /team\s*total/i.test(rawBlob)
  );
}

function getValueTeamTotalSideSafety(bet, marketLabel = "") {
  const ov = bet?.offerVerification && typeof bet.offerVerification === "object" ? bet.offerVerification : {};
  const isTeamTotalLike = isValueTeamTotalLikeMarket(bet, marketLabel);
  const valueTeamTotalSide = normalizeValueTeamTotalSide(ov.valueTeamTotalSide ?? bet?.valueTeamTotalSide);
  const rawTeamTotalSide = normalizeValueTeamTotalSide(ov.rawTeamTotalSide ?? bet?.rawTeamTotalSide);
  const valueTeamTotalDirection = normalizeValueTeamTotalDirection(ov.valueTeamTotalDirection ?? bet?.valueTeamTotalDirection);
  const rawTeamTotalDirection = normalizeValueTeamTotalDirection(ov.rawTeamTotalDirection ?? bet?.rawTeamTotalDirection);
  const teamTotalSideVerifiedFlag = ov.teamTotalSideVerified === true || bet?.teamTotalSideVerified === true;
  const sideMismatch = Boolean(valueTeamTotalSide && rawTeamTotalSide && valueTeamTotalSide !== rawTeamTotalSide);
  const directionMismatch = Boolean(
    valueTeamTotalDirection &&
      rawTeamTotalDirection &&
      valueTeamTotalDirection !== rawTeamTotalDirection
  );
  const sideForDisplay = rawTeamTotalSide || valueTeamTotalSide;
  const sideTrusted = Boolean(
    isTeamTotalLike &&
      teamTotalSideVerifiedFlag &&
      sideForDisplay &&
      !sideMismatch &&
      !directionMismatch
  );
  const isRawTeamTotalSafe = Boolean(
    isTeamTotalLike &&
      sideTrusted &&
      ov.status === "verified" &&
      bet?.isRawOfferVerified === true
  );
  return {
    isTeamTotalLike,
    teamTotalSideVerified: teamTotalSideVerifiedFlag,
    valueTeamTotalSide,
    rawTeamTotalSide,
    valueTeamTotalDirection,
    rawTeamTotalDirection,
    sideMismatch,
    directionMismatch,
    sideForDisplay,
    sideTrusted,
    isRawTeamTotalSafe,
  };
}

function pickTeamTotalDirectionForDisplay(bet) {
  const ov = bet?.offerVerification && typeof bet.offerVerification === "object" ? bet.offerVerification : {};
  const candidates = [
    ov.valueTeamTotalDirection,
    ov.rawTeamTotalDirection,
    bet?.valueTeamTotalDirection,
    bet?.rawTeamTotalDirection,
    bet?.display_selection,
    bet?.displaySelection,
    bet?.selection,
    bet?.selection_type,
    bet?.selectionType,
    bet?.pick,
    bet?.outcome,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const dir = normalizeValueTeamTotalDirection(candidates[i]);
    if (dir) return dir;
  }
  return "";
}

function formatTeamTotalDirectionLabel(direction, language) {
  const dir = normalizeValueTeamTotalDirection(direction);
  if (dir === "OVER") return translateSelectionDisplay("over", language) || mapFixtureBetToLabel("over", language) || "Tài";
  if (dir === "UNDER") return translateSelectionDisplay("under", language) || mapFixtureBetToLabel("under", language) || "Xỉu";
  return "";
}

function pickValueTeamNameBySide(bet, side) {
  const s = normalizeValueTeamTotalSide(side);
  if (s === "HOME") {
    return String(bet?.home_team ?? bet?.homeTeam ?? bet?.homeTeamName ?? "").trim();
  }
  if (s === "AWAY") {
    return String(bet?.away_team ?? bet?.awayTeam ?? bet?.awayTeamName ?? "").trim();
  }
  return "";
}

function buildSafeTeamTotalSelectionLabel(bet, language, safety, mode = "display") {
  const safe = safety || getValueTeamTotalSideSafety(bet);
  const dirLabel = formatTeamTotalDirectionLabel(
    safe.valueTeamTotalDirection || safe.rawTeamTotalDirection || pickTeamTotalDirectionForDisplay(bet),
    language
  );
  const line = pickLineFromValueBet(bet);
  const core = [dirLabel, isMeaningfulLineForCard(line) ? String(line).trim() : ""].filter(Boolean).join(" ").trim();
  const neutralSuffix = mode === "copy" ? "cần kiểm đội" : "kiểm tra đội";
  if (!safe.sideTrusted) {
    return core ? `${core} — ${neutralSuffix}` : neutralSuffix;
  }
  const teamName = pickValueTeamNameBySide(bet, safe.sideForDisplay);
  if (!teamName) return core ? `${core} — ${neutralSuffix}` : neutralSuffix;
  return [teamName, core].filter(Boolean).join(" ").trim();
}

/** Canonical EV threshold options (percent). */
const FULL_EV_OPTIONS = [1, 3, 5, 8, 10, 15];

const PLAN_KEYS = new Set(["basic", "pro", "premium"]);

/**
 * Normalizes an incoming account plan string to a canonical plan.
 * Missing or invalid values default to "basic".
 */
function normalizeAnalysisPlan(raw) {
  const p = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (PLAN_KEYS.has(p)) return p;
  return "basic";
}

/**
 * Returns allowed EV filter options (percent thresholds) for a canonical plan.
 * Extend switch cases as new plan rules are added.
 */
function getAllowedEvOptionsForPlan(canonicalPlan) {
  const plan = normalizeAnalysisPlan(canonicalPlan);
  switch (plan) {
    case "basic":
      return [...FULL_EV_OPTIONS];
    case "pro":
      return [...FULL_EV_OPTIONS];
    case "premium":
      return [...FULL_EV_OPTIONS];
    default:
      return [...FULL_EV_OPTIONS];
  }
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Decimal odds → American (fair_probability / legacy decimal fallbacks only). */
function americanFromDecimal(dec) {
  const d = toFiniteNumber(dec);
  if (d == null || d <= 1) return null;
  if (d >= 2) return Math.round((d - 1) * 100);
  return Math.round(-100 / (d - 1));
}

function valueBetOfferedOddsToDecimal(bet) {
  if (!bet || typeof bet !== "object") return null;
  const raw =
    bet.odds ??
    bet.oddsDecimal ??
    bet.odds_decimal ??
    null;
  const n = toFiniteNumber(raw);
  if (n == null) return null;
  if (n >= 100) return 1 + n / 100;
  if (n <= -100) return 1 + 100 / Math.abs(n);
  if (n > 0 && n < 1) return 1 + n;
  if (n > 1) return n;
  return null;
}

function valueBetOfferedOddsToHkNumber(bet) {
  const dec = valueBetOfferedOddsToDecimal(bet);
  if (dec == null || !Number.isFinite(dec) || dec <= 1) return null;
  const hk = dec - 1;
  return Number.isFinite(hk) && hk > 0 ? hk : null;
}

/** Display-only: infer decimal odds from raw trueOdds (decimal, HK, or American). */
function trueOddsDecimalForDisplay(value) {
  const n = toFiniteNumber(value);
  if (n == null) return null;
  if (n > 0 && n < 1) return 1 + n;
  if (n <= -100) return 1 + 100 / Math.abs(n);
  if (n >= 100) return 1 + n / 100;
  if (n > 1) return n;
  return null;
}

function formatTrueOddsHkDisplay(value, fmt = "hk") {
  const dec = trueOddsDecimalForDisplay(value);
  if (dec != null && Number.isFinite(dec)) {
    const formatted = formatAnalysisOddsDisplay(dec, fmt);
    if (formatted != null) return formatted;
    const hk = dec - 1;
    if (Number.isFinite(hk)) {
      const fixed = hk.toFixed(3);
      return fixed.replace(/(?:\.0+|(\.\d*?)0+)$/, "$1");
    }
  }
  if (value === undefined || value === null) return "";
  return String(value);
}

function buildValueSemanticMarketDisplay(bet, lang) {
  const fn = sharpValueDisplayAdapter.buildValueSemanticMarketLabel;
  if (typeof fn !== "function") return "";
  return String(fn(bet, lang) || "").trim();
}

function buildValueSemanticSelectionDisplay(bet, lang) {
  const fn = sharpValueDisplayAdapter.buildValueSemanticSelectionLabel;
  if (typeof fn !== "function") return "";
  return String(fn(bet, lang) || "").trim();
}

/**
 * True when a candidate display label still contains raw Sharp market_type
 * tokens (snake_case or untranslated keys). Used so the page only falls back
 * to the sharpValueAdapter semantic helper when the analysisTranslate-produced
 * label is unusable as UI text.
 */
function looksLikeRawMarketKey(label) {
  const s = String(label ?? "").trim();
  if (!s) return false;
  if (/_/.test(s)) return true;
  if (
    /\b(total[_\s-]*corners|team[_\s-]*total[_\s-]*corners|1st[_\s-]*half[_\s-]*total[_\s-]*corners|2nd[_\s-]*half[_\s-]*total[_\s-]*corners|total[_\s-]*goals|team[_\s-]*total[_\s-]*goals|total[_\s-]*cards|team[_\s-]*total[_\s-]*cards|moneyline|match[_\s-]*winner|point[_\s-]*spread|asian[_\s-]*handicap|set[_\s-]*handicap|game[_\s-]*spread|market[_\s-]*type)\b/i.test(
      s
    )
  ) {
    return true;
  }
  return false;
}

function humanizeRawMarketLabel(label) {
  const fn = sharpValueDisplayAdapter.humanizeSnakeCaseForDisplay;
  if (typeof fn === "function") {
    const out = String(fn(label) || "").trim();
    if (out) return out;
  }
  return String(label ?? "").trim();
}

function buildValueDisplayLineText(bet) {
  const fn = sharpValueDisplayAdapter.pickValueDisplayLine;
  if (typeof fn !== "function") return "";
  return String(fn(bet) || "").trim();
}

function buildValueOfferedOddsHkDisplay(bet) {
  const fn = sharpValueDisplayAdapter.buildValueDisplayOddsHk;
  if (typeof fn !== "function") return "";
  return String(fn(bet) || "").trim();
}

function buildValueTrueOddsHkDisplay(bet) {
  const fn = sharpValueDisplayAdapter.buildValueDisplayTrueOddsHk;
  if (typeof fn !== "function") return "";
  return String(fn(bet) || "").trim();
}

// ---------------------------------------------------------------------------
// Generic Value display helpers (Task A / B / D).
//
// Thin wrappers around the sharpValueAdapter helpers so the page can call them
// without re-implementing the family detection / line-formatting rules.
// `verifiedRaw` is the bet's `offerVerification` payload when the row is
// fully raw-verified — its `matched*` fields then become the source of truth
// for sportsbook / market / selection / line / odds display.
// ---------------------------------------------------------------------------
function getValueRowVerifiedRaw(bet) {
  const fn = sharpValueDisplayAdapter.readVerifiedRawFromRow;
  if (typeof fn !== "function") return null;
  return fn(bet) || null;
}

function getValueRowMarketFamily(bet, verifiedRaw) {
  const fn = sharpValueDisplayAdapter.getValueMarketFamily;
  if (typeof fn !== "function") return "";
  return String(fn(bet, verifiedRaw) || "").trim();
}

function formatValueRowDisplayLine(bet, verifiedRaw) {
  const fn = sharpValueDisplayAdapter.formatValueDisplayLine;
  if (typeof fn !== "function") return "";
  return String(fn(bet, verifiedRaw) || "").trim();
}

function buildValueDisplayMarketLabelEx(bet, lang, verifiedRaw) {
  const fn = sharpValueDisplayAdapter.buildValueDisplayMarketLabel;
  if (typeof fn !== "function") return "";
  return String(fn(bet, lang, verifiedRaw) || "").trim();
}

function buildValueDisplaySelectionLabelEx(bet, verifiedRaw, lang) {
  const fn = sharpValueDisplayAdapter.buildValueDisplaySelectionLabel;
  if (typeof fn !== "function") return "";
  return String(fn(bet, verifiedRaw, lang) || "").trim();
}

function buildValueCanonicalDisplayEx(bet, lang) {
  const fn = sharpValueDisplayAdapter.buildValueCanonicalDisplay;
  if (typeof fn !== "function") return null;
  const out = fn(bet, lang);
  return out && typeof out === "object" ? out : null;
}

function resolveValueTeamTotalDisplaySideEx(bet, verifiedRaw) {
  const fn = sharpValueDisplayAdapter.resolveValueTeamTotalDisplaySide;
  if (typeof fn !== "function") return null;
  const out = fn(bet, verifiedRaw);
  return out && typeof out === "object" ? out : null;
}

/**
 * Translate a SharpAPI structured `market_type` key to the Value-tab family
 * label (per analysisTranslate Task G map). Returns "" when the key is not
 * one of the explicitly-supported keys so callers fall back gracefully.
 */
function translateValueMarketFamilyKeySafe(key, lang) {
  if (typeof translateValueMarketFamilyKey !== "function") return "";
  return String(translateValueMarketFamilyKey(key, lang) || "").trim();
}

function clampToNearestNumberOption(value, options) {
  const opts = Array.isArray(options) ? options : [];
  if (!opts.length) return value;
  const v = Number(value);
  if (!Number.isFinite(v)) return opts[0];

  let best = opts[0];
  let bestDiff = Math.abs(opts[0] - v);

  for (let i = 1; i < opts.length; i += 1) {
    const opt = opts[i];
    const diff = Math.abs(opt - v);
    if (diff < bestDiff) {
      best = opt;
      bestDiff = diff;
    } else if (diff === bestDiff && opt > best) {
      // Tie-breaker: pick the higher value to be stricter.
      best = opt;
      bestDiff = diff;
    }
  }

  return best;
}

function parseDetectedAtMs(v) {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim()) {
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

function isUsableKickoffTs(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return false;
  const a = Math.abs(value);
  let ms;
  if (a >= 1e12) ms = value;
  else if (a >= 1e9) ms = Math.round(value * 1000);
  else return false;
  return !isSuspiciousEpochLikeMs(ms);
}

function isSuspiciousKickoffDisplayText(s) {
  const t = String(s || "").trim();
  if (!t) return true;
  if (/1970|01\s*Jan|Jan\s*01|01\/01\/1970|1970-01-01|01\.01\.1970/i.test(t)) return true;
  return false;
}

function isUsableKickoffString(value) {
  const t = String(value ?? "").trim();
  if (!t) return false;
  return !isSuspiciousKickoffDisplayText(t);
}

function isBadCardTimeDisplay(str) {
  if (!str || str === "—") return true;
  if (/1970|01\s*Jan|Jan\s*01|1970-01-01/i.test(str)) return true;
  return false;
}

/** Prematch time pill repair: same rules as Middles `MiddleCard` (live path unchanged). */
function computeValueBetCardDisplayTimeValue(statusInfo, bet, lang, timeZone) {
  let displayTimeValue = statusInfo.timeValue;
  if (statusInfo.isLive) {
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "LIVE";
    }
    return displayTimeValue;
  }
  if (isBadCardTimeDisplay(displayTimeValue)) {
    displayTimeValue = "—";
  }
  if (!displayTimeValue || displayTimeValue === "—") {
    const rawKickoff = bet?.kickoffDate ?? bet?.kickoff ?? "";
    if (isUsableKickoffString(rawKickoff)) {
      displayTimeValue = formatAnalysisTime(rawKickoff, lang, timeZone);
    } else if (isUsableKickoffTs(bet?.kickoffTs)) {
      const a = Math.abs(bet.kickoffTs);
      const ms = a >= 1e12 ? bet.kickoffTs : Math.round(bet.kickoffTs * 1000);
      displayTimeValue = formatAnalysisTime(ms, lang, timeZone);
    } else {
      displayTimeValue = "—";
    }
  }
  if (isBadCardTimeDisplay(displayTimeValue)) {
    displayTimeValue = "—";
  }
  return displayTimeValue;
}

function normalizeIncomingValueBet(raw) {
  if (!raw || typeof raw !== "object") return null;

  const fixtureIdFromNested = raw?.fixture?.id ?? null;
  const marketTypeFromNested = raw?.market?.type ?? raw?.market?.marketType ?? null;
  const bookmakerFromNested = raw?.bookmaker?.name ?? raw?.bookmaker?.key ?? null;
  const selectionFromNested =
    typeof raw?.selection === "string" ? null : raw?.selection?.name ?? raw?.selection?.key ?? null;
  const homeFromNested = raw?.fixture?.teams?.home?.name ?? raw?.fixture?.teams?.home ?? null;
  const awayFromNested = raw?.fixture?.teams?.away?.name ?? raw?.fixture?.teams?.away ?? null;

  const rawId = raw.id ?? raw.bet_id ?? raw.valueBetId ?? raw.value_bet_id ?? null;
  const derivedIdParts = [
    raw.fixture_id ?? raw.fixtureId ?? raw.game_id ?? fixtureIdFromNested,
    raw.market_type ?? raw.marketType ?? marketTypeFromNested,
    raw.line ?? raw?.market?.line ?? raw?.selection?.line,
    typeof raw.selection === "string" ? raw.selection : selectionFromNested,
    raw.bookmaker ?? raw.sportsbook ?? raw.bookmaker_name ?? raw.bookmakerName ?? bookmakerFromNested,
  ]
    .map((v) => (v === undefined || v === null || v === "" ? null : String(v).trim()))
    .filter(Boolean);
  const derivedId = derivedIdParts.length ? derivedIdParts.join("|") : null;
  const id = rawId ?? derivedId ?? null;

  const homeTeam = raw.home_team ?? raw.homeTeam ?? homeFromNested ?? null;
  const awayTeam = raw.away_team ?? raw.awayTeam ?? awayFromNested ?? null;
  const matchTrim = String(raw.match ?? "").trim();
  const gameTrim = String(raw.game ?? "").trim();
  const match =
    matchTrim ||
    gameTrim ||
    (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : homeTeam || awayTeam || "");

  const ev = toFiniteNumber(raw.ev ?? raw.EV ?? raw.ev_percentage ?? raw.edge);
  const odds = toFiniteNumber(
    raw.odds ?? raw.oddsDecimal ?? raw.odds_decimal ?? raw.odd ?? raw.price ?? raw.offeredOdd ?? raw.offered_odd ?? null
  );
  const explicitTrue = toFiniteNumber(raw.trueOdds);
  let trueOdds;
  if (explicitTrue != null) {
    trueOdds = explicitTrue;
  } else {
    const noVigAmerican = toFiniteNumber(raw.noVigOdds ?? raw.no_vig_odds);
    if (noVigAmerican != null) {
      trueOdds = noVigAmerican;
    } else {
      const fairP = toFiniteNumber(raw.fairProbability ?? raw.fair_probability);
      const fairDec =
        fairP != null && fairP > 0 && fairP <= 1 ? 1 / fairP : null;
      if (fairDec != null) {
        trueOdds = americanFromDecimal(fairDec);
      } else {
        const legacyDec = toFiniteNumber(
          raw.true_odds ?? raw.fair_odds ?? raw.fairOdds ?? raw.fairOdd ?? raw.fair_odd ?? null
        );
        trueOdds = legacyDec != null ? americanFromDecimal(legacyDec) : null;
      }
    }
  }

  const isLive = raw.isLive ?? raw.is_live ?? raw.live ?? false;

  const detectedAtMs = parseDetectedAtMs(raw.detected_at);
  const timestamp = toFiniteNumber(
    raw.timestamp ??
      raw.ts ??
      detectedAtMs ??
      raw.last_update ??
      raw.lastUpdate ??
      raw.updated_ts ??
      raw.updatedTs ??
      raw.processedAt ??
      raw.processed_at ??
      null
  );

  const flatSelection = (() => {
    if (typeof raw.selection === "string") {
      const s = raw.selection.trim();
      return s || null;
    }
    const ds =
      (typeof raw.display_selection === "string" ? raw.display_selection : null) ??
      (typeof raw.displaySelection === "string" ? raw.displaySelection : null);
    if (ds && ds.trim()) return ds.trim();
    const nested = raw.selection && typeof raw.selection === "object" ? raw.selection : null;
    const nestedName = typeof nested?.name === "string" ? nested.name.trim() : "";
    if (nestedName) return nestedName;
    const nestedKey = typeof nested?.key === "string" ? nested.key.trim() : "";
    if (nestedKey) return nestedKey;
    const pick = typeof raw.pick === "string" ? raw.pick.trim() : "";
    if (pick) return pick;
    const outc = typeof raw.outcome === "string" ? raw.outcome.trim() : "";
    if (outc) return outc;
    const outcName = typeof raw.outcomeName === "string" ? raw.outcomeName.trim() : "";
    if (outcName) return outcName;
    if (selectionFromNested && String(selectionFromNested).trim()) return String(selectionFromNested).trim();
    return null;
  })();

  const out = {
    ...raw,
    id,
    match,
    fixture_id: raw.fixture_id ?? raw.fixtureId ?? raw.game_id ?? fixtureIdFromNested ?? null,
    league: raw.league ?? raw?.fixture?.league?.name ?? raw?.fixture?.league ?? null,
    market_type: raw.market_type ?? raw.marketType ?? marketTypeFromNested ?? raw.market_type,
    market: raw.market ?? raw.market_type ?? raw.marketType ?? marketTypeFromNested ?? null,
    bookmaker:
      raw.bookmaker ?? raw.sportsbook ?? raw.bookmaker_name ?? raw.bookmakerName ?? bookmakerFromNested ?? null,
    sharp_book: raw.sharp_book ?? raw.sharpBook ?? raw.devig_book ?? raw.devigBook ?? null,
    sharpBook: raw.sharp_book ?? raw.sharpBook ?? raw.devig_book ?? raw.devigBook ?? null,
    devig_book: raw.devig_book ?? raw.devigBook ?? raw.sharp_book ?? raw.sharpBook ?? null,
    devigBook: raw.devig_book ?? raw.devigBook ?? raw.sharp_book ?? raw.sharpBook ?? null,
    referenceBook: raw.sharp_book ?? raw.sharpBook ?? raw.devig_book ?? raw.devigBook ?? null,
    odds: odds ?? raw.odds ?? null,
    trueOdds: trueOdds ?? null,
    ev: ev,
    isLive: isLive === true,
    timestamp: timestamp,
    ts: timestamp,
    selection: flatSelection,
    sport: raw.sport ?? raw.sportKey ?? raw.sportName ?? raw.category ?? raw.gameType ?? raw?.fixture?.sport?.name ?? null,
    game: raw.game ?? raw.gameTitle ?? raw.game_name ?? null,
    map: raw.map ?? raw.mapName ?? raw.map_name ?? null,
  };

  if (Object.prototype.hasOwnProperty.call(raw, "selection_type")) out.selection_type = raw.selection_type;
  if (Object.prototype.hasOwnProperty.call(raw, "selectionType")) out.selectionType = raw.selectionType;
  if (Object.prototype.hasOwnProperty.call(raw, "display_selection")) out.display_selection = raw.display_selection;
  if (Object.prototype.hasOwnProperty.call(raw, "displaySelection")) out.displaySelection = raw.displaySelection;
  if (Object.prototype.hasOwnProperty.call(raw, "market_type")) out.market_type = raw.market_type;
  if (Object.prototype.hasOwnProperty.call(raw, "marketType")) out.marketType = raw.marketType;
  if (Object.prototype.hasOwnProperty.call(raw, "display_market")) out.display_market = raw.display_market;
  if (Object.prototype.hasOwnProperty.call(raw, "displayMarket")) out.displayMarket = raw.displayMarket;

  // Canonical kickoff / schedule aliases (real kickoff sources only — never detection timestamps).
  const fx = raw.fixture && typeof raw.fixture === "object" ? raw.fixture : {};
  const eventObj =
    raw.event && typeof raw.event === "object"
      ? raw.event
      : fx.event && typeof fx.event === "object"
        ? fx.event
        : {};

  const kickoffStrCandidates = [
    typeof raw.kickoffDate === "string" ? raw.kickoffDate : null,
    typeof raw.kickoff === "string" ? raw.kickoff : null,
    typeof raw.start_time === "string" ? raw.start_time : null,
    typeof raw.startTime === "string" ? raw.startTime : null,
    typeof raw.event_start_time === "string" ? raw.event_start_time : null,
    typeof raw.eventStartTime === "string" ? raw.eventStartTime : null,
    typeof fx.kickoffDate === "string" ? fx.kickoffDate : null,
    typeof fx.kickoff === "string" ? fx.kickoff : null,
    typeof fx.start_time === "string" ? fx.start_time : null,
    typeof fx.startTime === "string" ? fx.startTime : null,
    typeof fx.date === "string" ? fx.date : null,
    typeof fx.dateTime === "string" ? fx.dateTime : null,
    typeof fx.datetime === "string" ? fx.datetime : null,
    typeof eventObj.dateTime === "string" ? eventObj.dateTime : null,
    typeof eventObj.startsAt === "string" ? eventObj.startsAt : null,
    typeof eventObj.startTime === "string" ? eventObj.startTime : null,
    typeof eventObj.event_start_time === "string" ? eventObj.event_start_time : null,
    typeof eventObj.eventStartTime === "string" ? eventObj.eventStartTime : null,
  ];

  let resolvedKickoffStr = "";
  for (let i = 0; i < kickoffStrCandidates.length; i += 1) {
    const cand = kickoffStrCandidates[i];
    if (cand && isUsableKickoffString(cand)) {
      resolvedKickoffStr = cand.trim();
      break;
    }
  }

  const kickoffTsCandidates = [raw.kickoffTs, fx.kickoffTs, eventObj?.kickoffTs].filter((v) => v != null);
  let resolvedKickoffTs = null;
  for (let i = 0; i < kickoffTsCandidates.length; i += 1) {
    const v = kickoffTsCandidates[i];
    if (isUsableKickoffTs(v)) {
      resolvedKickoffTs = v;
      break;
    }
  }

  if (resolvedKickoffTs == null && resolvedKickoffStr) {
    const d = parseAnalysisInstant(resolvedKickoffStr);
    if (d && !isSuspiciousEpochLikeDate(d)) {
      resolvedKickoffTs = d.getTime();
    }
  }

  if (!resolvedKickoffStr && resolvedKickoffTs != null && isUsableKickoffTs(resolvedKickoffTs)) {
    try {
      const a = Math.abs(resolvedKickoffTs);
      const ms = a >= 1e12 ? resolvedKickoffTs : Math.round(resolvedKickoffTs * 1000);
      resolvedKickoffStr = new Date(ms).toISOString();
    } catch {
      resolvedKickoffStr = "";
    }
  }

  const koAliasStr =
    resolvedKickoffStr && isUsableKickoffString(resolvedKickoffStr) ? resolvedKickoffStr.trim() : "";
  if (koAliasStr) {
    out.start_time = koAliasStr;
    out.startTime = koAliasStr;
    out.event_start_time = koAliasStr;
    out.eventStartTime = koAliasStr;
    out.kickoffDate = koAliasStr;
    out.kickoff = koAliasStr;
  }

  if (resolvedKickoffTs != null && isUsableKickoffTs(resolvedKickoffTs)) {
    out.kickoffTs = resolvedKickoffTs;
  } else if (!isUsableKickoffTs(out.kickoffTs)) {
    delete out.kickoffTs;
  }

  const timeZoneResolved =
    (typeof raw.timeZone === "string" && raw.timeZone.trim()) ||
    (typeof raw.time_zone === "string" && raw.time_zone.trim()) ||
    (typeof raw.timezone === "string" && raw.timezone.trim()) ||
    (typeof fx.timeZone === "string" && fx.timeZone.trim()) ||
    (typeof fx.timezone === "string" && fx.timezone.trim()) ||
    (typeof fx.time_zone === "string" && fx.time_zone.trim()) ||
    (typeof eventObj.timeZone === "string" && eventObj.timeZone.trim()) ||
    (typeof eventObj.timezone === "string" && eventObj.timezone.trim()) ||
    "";
  if (timeZoneResolved) {
    const tz = String(timeZoneResolved);
    out.timeZone = tz;
    out.time_zone = tz;
  }

  return out;
}

function isMeaningfulLineForCard(line) {
  const s = line == null ? "" : String(line).trim();
  if (!s) return false;
  if (s === "—" || s === "-") return false;
  return true;
}

function selectionAlreadyContainsLine(sel, line) {
  const rawSel = String(sel ?? "").trim();
  const rawLine = String(line ?? "").trim();
  if (!rawSel || !rawLine) return false;
  if (!isMeaningfulLineForCard(rawLine)) return false;
  const lineCore = rawLine.replace(/\s+/g, "");
  if (!lineCore) return false;
  const escaped = lineCore.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[^0-9.,])${escaped}($|[^0-9])`, "i");
  return re.test(rawSel);
}

function isValuePlayerPropLikeRow(bet) {
  return Boolean(
    bet?.is_player_prop === true ||
      bet?.isPlayerProp === true ||
      String(bet?.player_name || bet?.playerName || "").trim() ||
      String(bet?.stat_category || bet?.statCategory || "").trim() ||
      /player|rebounds|assists|points|threes|blocks|steals|shots|saves|strikeouts|aces/i.test(
        [
          bet?.market_type,
          bet?.marketType,
          bet?.market,
          bet?.display_market,
          bet?.displayMarket,
          bet?.stat_category,
          bet?.statCategory,
        ]
          .filter(Boolean)
          .join(" ")
      )
  );
}

function isDirectionLikeSelectionLabel(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return false;
  const normalized = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[|/_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return false;
  const compact = normalized.replace(/\s+/g, "");
  return (
    /^(over|under|tai|xiu)$/.test(compact) ||
    /^(home|away)\s+(over|under)$/.test(normalized) ||
    /^(over|under)\s+(home|away)$/.test(normalized)
  );
}

function pickLineFromValueBet(bet) {
  const v =
    bet?.line ??
    bet?.market?.line ??
    bet?.selection?.line ??
    bet?.point ??
    bet?.handicap ??
    bet?.total ??
    "";
  return v == null ? "" : String(v).trim();
}

function marketStringLooksLikePlainSelectionFragment(m) {
  const s = String(m ?? "").trim().toLowerCase();
  if (!s) return false;
  if (/^(over|under)$/.test(s)) return true;
  if (/^(home|away)[_\s]+(over|under)$/.test(s)) return true;
  if (/^(over|under)\s+[-+]?\d/.test(s)) return true;
  return false;
}

function marketTypeLooksStructuredSharp(mt) {
  const s = String(mt ?? "").trim();
  if (!s) return false;
  if (s.includes("_")) return true;
  return /^(1st|2nd|first|second)\b/i.test(s);
}

function pickBestRawMarketSource(bet) {
  if (!bet || typeof bet !== "object") return "";
  const m = String(bet.market ?? "").trim();
  const mt = String(bet.market_type ?? bet.marketType ?? "").trim();
  if (!mt) return m;
  if (!m) return mt;
  if (marketStringLooksLikePlainSelectionFragment(m)) return mt;
  if (marketTypeLooksStructuredSharp(mt)) return mt;
  if (/\b(1st|2nd|first|second)\s+half\b/i.test(m) && !/\b(1st|2nd|first|second)\s+half\b/i.test(mt)) return m;
  return m || mt;
}

function normalizePeriodScanText(value) {
  return String(value ?? "")
    .trim()
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ");
}

/**
 * True when text clearly refers to a quarter / set / map / inning style segment
 * (not soccer halves), so we should not infer HT/FT from loose tokens.
 */
function textImpliesNonHalfSportSegment(normalizedText) {
  const n = normalizePeriodScanText(normalizedText).toLowerCase();
  if (!n) return false;
  if (/\b(1st|2nd|3rd|4th|first|second|third|fourth)\s+quarter\b/.test(n)) return true;
  if (/\bquarter\b/.test(n) && /\b(q[1-4]|[1-4](st|nd|rd|th))\b/.test(n)) return true;
  if (/\bq[1-4]\b/i.test(n)) return true;
  if (/\b(1st|2nd)\s+half\b/.test(n) || /\bfirst\s+half\b/.test(n) || /\bsecond\s+half\b/.test(n)) return false;
  if (/\b(inning|innings)\b/.test(n)) return true;
  if (/\bmap\s*\d+/.test(n)) return true;
  if (/\bset\s*[1-9]\b/.test(n) || /\b[1-9](?:st|nd|rd|th)\s+set\b/.test(n)) return true;
  return false;
}

/**
 * From translated or raw market (or concatenated market fields): HT, 2H, FT, or "".
 * Does not default full match; only explicit phrasing / common abbreviations.
 */
function extractMarketPeriodSuffix(labelOrRawMarket) {
  const normalized = normalizePeriodScanText(labelOrRawMarket);
  if (!normalized) return "";

  const lower = normalized.toLowerCase();
  const nonHalfSegment = textImpliesNonHalfSportSegment(normalized);

  const patterns = [
    { re: /\bsecond\s+half\b/gi, out: "2H" },
    { re: /\b2nd\s+half\b/gi, out: "2H" },
    { re: /\bsecond_half\b/gi, out: "2H" },
    { re: /\b(?:^|[\s_/])2h(?:$|[\s_/])/gi, out: "2H" },
    { re: /\bfirst\s+half\b/gi, out: "HT" },
    { re: /\b1st\s+half\b/gi, out: "HT" },
    { re: /\bfirst_half\b/gi, out: "HT" },
    { re: /\bfirst[\s_-]*half\b/gi, out: "HT" },
    { re: /\b(?:^|[\s_/])1h(?:$|[\s_/])/gi, out: "HT" },
    { re: /\b(?:^|[\s_/])ht(?:$|[\s_/])/gi, out: "HT" },
    { re: /\bfull[\s_-]*match\b/gi, out: "FT" },
    { re: /\bfull[\s_-]*game\b/gi, out: "FT" },
    { re: /\bfull\s+time\b/gi, out: "FT" },
    { re: /\bfulltime\b/gi, out: "FT" },
    { re: /\b(?:^|[\s_/])ft(?:$|[\s_/])/gi, out: "FT" },
  ];

  let bestIndex = -1;
  let bestOut = "";
  for (const { re, out } of patterns) {
    const r = new RegExp(re.source, re.flags);
    let m;
    while ((m = r.exec(normalized)) !== null) {
      if (m.index >= bestIndex) {
        bestIndex = m.index;
        bestOut = out;
      }
    }
  }

  if (!bestOut) return "";
  if (nonHalfSegment && (bestOut === "HT" || bestOut === "2H")) {
    const halfExplicit =
      /\bfirst\s+half\b/i.test(lower) ||
      /\b1st\s+half\b/i.test(lower) ||
      /\bsecond\s+half\b/i.test(lower) ||
      /\b2nd\s+half\b/i.test(lower) ||
      /\bfirst_half\b/i.test(lower) ||
      /\bsecond_half\b/i.test(lower);
    if (!halfExplicit) return "";
  }
  return bestOut;
}

function appendMarketPeriodSuffixIfMissing(line, suffix) {
  const s = String(line ?? "").trim();
  const suf = String(suffix ?? "").trim();
  if (!suf) return s;
  if (!s) return suf;
  const tokens = suf
    .trim()
    .split(/\s+/)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("\\s+");
  const endRe = new RegExp(`(?:^|\\s)${tokens}$`, "i");
  if (endRe.test(s)) return s;
  return `${s} ${suf}`.trim();
}

/** Maps legacy long labels or short tokens to HT / 2H / FT for the end of the line. */
function toShortPeriodTokenFromTextLabel(longOrShort) {
  const s = String(longOrShort ?? "").trim();
  if (!s) return "";
  const k = s.toLowerCase().replace(/\s+/g, " ");
  if (k === "full time" || k === "fulltime") return "FT";
  if (k === "first half") return "HT";
  if (k === "second half") return "2H";
  const u = s.toUpperCase();
  if (u === "FT" || u === "HT" || u === "2H") return u;
  if (u === "1H") return "HT";
  return s;
}

function stringifyForPeriodScan(value) {
  if (value === undefined || value === null || typeof value === "boolean") return "";
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string") return value.trim();
  if (typeof value !== "object") return String(value).trim();
  const gs = value;
  const chunks = [];
  const add = (v) => {
    if (v === undefined || v === null || typeof v === "boolean") return;
    if (typeof v === "number" && Number.isFinite(v)) chunks.push(String(v));
    else if (typeof v === "string" && v.trim()) chunks.push(v.trim());
  };
  add(gs.period);
  add(gs.currentPeriod);
  add(gs.half);
  add(gs.phase);
  add(gs.clock);
  add(gs.status);
  add(gs.match_status);
  add(gs.matchStatus);
  add(gs.description);
  add(gs.state);
  return chunks.join(" ").trim();
}

function collectValueBetPeriodFieldBlob(bet) {
  if (!bet || typeof bet !== "object") return "";
  const parts = [];
  const add = (v) => {
    if (v === undefined || v === null || typeof v === "boolean") return;
    const s =
      typeof v === "object" && v !== null
        ? stringifyForPeriodScan(v)
        : typeof v === "number" && Number.isFinite(v)
          ? String(v)
          : String(v).trim();
    if (s) parts.push(s);
  };
  add(bet.market);
  add(bet.market_type);
  add(bet.marketType);
  add(bet.market_id);
  add(bet.marketId);
  const keys = [
    "period",
    "current_period",
    "currentPeriod",
    "half",
    "quarter",
    "current_quarter",
    "currentQuarter",
    "set",
    "current_set",
    "currentSet",
    "setNumber",
    "phase",
    "status",
    "match_status",
    "matchStatus",
    "game_status",
    "gameStatus",
    "game_state",
    "gameState",
    "status_text",
    "statusText",
    "minute",
    "elapsed",
    "clock",
    "match_clock",
    "matchClock",
    "time_remaining",
    "timeRemaining",
  ];
  for (let i = 0; i < keys.length; i += 1) add(bet[keys[i]]);
  return parts.join(" ").toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * True when a period token from structured/live data is quarter / inning / map / set / unknown code —
 * must not be turned into HT/FT.
 */
function structuredPeriodTokenBlocksHalfInference(token) {
  const raw = String(token ?? "").trim();
  if (!raw) return false;
  const t = raw.toUpperCase();
  if (/^Q[1-4]$/.test(t)) return true;
  if (/^T\d+$/i.test(t)) return true;
  if (/^B\d+$/i.test(t)) return true;
  if (/^S[1-9]$/.test(t)) return true;
  if (/^M[1-9]$/.test(t)) return true;
  if (/^\d{2,}$/.test(t)) return true;
  return false;
}

function inferStructuredPeriodSuffix(bet) {
  if (!bet || typeof bet !== "object") return "";

  const primaryPeriod = String(
    bet.period ??
      bet.current_period ??
      bet.currentPeriod ??
      (bet.game_state && typeof bet.game_state === "object" ? bet.game_state.period : "") ??
      (bet.gameState && typeof bet.gameState === "object" ? bet.gameState.period : "") ??
      ""
  ).trim();
  if (structuredPeriodTokenBlocksHalfInference(primaryPeriod)) return "";

  const halfNum = toFiniteNumber(bet.half);
  if (halfNum === 1) return "HT";
  if (halfNum === 2) return "2H";
  const halfStr = String(bet.half ?? "").trim().toLowerCase();
  if (halfStr === "first" || halfStr === "1st" || halfStr === "h1") return "HT";
  if (halfStr === "second" || halfStr === "2nd" || halfStr === "h2") return "2H";

  const q =
    toFiniteNumber(bet.quarter) ??
    toFiniteNumber(bet.current_quarter) ??
    toFiniteNumber(bet.currentQuarter);
  if (q != null && Number.isInteger(q) && q >= 1 && q <= 4) return "";

  const setNum = toFiniteNumber(bet.setNumber);
  if (setNum != null && Number.isInteger(setNum) && setNum >= 1 && setNum <= 9) return "";

  const mapNum = toFiniteNumber(bet.mapNumber);
  if (mapNum != null && Number.isInteger(mapNum) && mapNum >= 1 && mapNum <= 9) return "";

  const blob = collectValueBetPeriodFieldBlob(bet);
  if (!blob) return "";

  const tokens = blob.split(/[\s,/|]+/).filter(Boolean);
  for (let i = 0; i < tokens.length; i += 1) {
    if (structuredPeriodTokenBlocksHalfInference(tokens[i])) return "";
  }

  const hasFull =
    /\bfull[\s_-]*time\b/.test(blob) ||
    /\bfulltime\b/.test(blob) ||
    /(?:^|[\s_/])ft(?:$|[\s_/])/.test(blob) ||
    /\bfull[\s_-]*match\b/.test(blob) ||
    /\bfull[\s_-]*game\b/.test(blob) ||
    /\baet\b/.test(blob);
  const hasFirst =
    /\bfirst[\s_-]+half\b/.test(blob) ||
    /\b1st[\s_-]+half\b/.test(blob) ||
    /\bfirst_half\b/.test(blob) ||
    /\b1st_half\b/.test(blob) ||
    /(?:^|[\s_/])1h(?:$|[\s_/])/.test(blob) ||
    /\bh1\b/.test(blob) ||
    /(?:^|[\s_/])ht(?:$|[\s_/])/.test(blob);
  const hasSecond =
    /\bsecond[\s_-]+half\b/.test(blob) ||
    /\b2nd[\s_-]+half\b/.test(blob) ||
    /\bsecond_half\b/.test(blob) ||
    /(?:^|[\s_/])2h(?:$|[\s_/])/.test(blob) ||
    /\bh2\b/.test(blob);

  if (hasFull && (hasFirst || hasSecond)) return "";
  if (hasFirst && hasSecond) return "";
  if (hasFull) return "FT";
  if (hasSecond) return "2H";
  if (hasFirst) return "HT";

  return "";
}

function periodSuffixFamily(token) {
  const t = String(token ?? "").trim().toUpperCase();
  if (!t) return "";
  if (t === "FT") return "FULL";
  if (t === "1H" || t === "HT") return "H1";
  if (t === "2H") return "H2";
  return t;
}

function mergeCompatiblePeriodSuffixes(a, b) {
  const sa = String(a ?? "").trim();
  const sb = String(b ?? "").trim();
  if (!sa) return sb;
  if (!sb) return sa;
  if (sa.toUpperCase() === sb.toUpperCase()) return sa;

  const fa = periodSuffixFamily(sa);
  const fb = periodSuffixFamily(sb);
  if (!fa || !fb) return sa;
  if (fa !== fb) return "";
  if (fa === "H1") return "HT";
  return sa;
}

function valueBetIsLiveForPeriodSuffix(bet) {
  if (!bet || typeof bet !== "object") return false;
  if (bet.isLive === true) return true;
  const st = String(bet.status ?? bet.matchStatus ?? bet.match_status ?? bet.state ?? bet.phase ?? "").toLowerCase();
  if (st.includes("live") || st.includes("inplay") || st.includes("in-play") || st.includes("playing")) return true;
  return false;
}

function liveSportSpecificPeriodBlocksHalfFt(bet) {
  if (!bet || typeof bet !== "object") return false;
  const p = String(
    bet.period ??
      bet.current_period ??
      bet.currentPeriod ??
      (bet.game_state && typeof bet.game_state === "object" ? bet.game_state.period : "") ??
      (bet.gameState && typeof bet.gameState === "object" ? bet.gameState.period : "") ??
      ""
  ).trim();
  return structuredPeriodTokenBlocksHalfInference(p);
}

function shouldOfferPrematchDefaultFt(bet, identifierBlob) {
  if (!bet || typeof bet !== "object") return false;
  const blob = normalizePeriodScanText(identifierBlob).toLowerCase();
  if (!blob) return false;
  if (/\bplayer\b/.test(blob)) return false;
  if (textImpliesNonHalfSportSegment(blob)) return false;
  if (/\bfirst\s+half\b/.test(blob) || /\b1st\s+half\b/.test(blob)) return false;
  if (/\bsecond\s+half\b/.test(blob) || /\b2nd\s+half\b/.test(blob)) return false;

  const cn = String(
    canonicalValueBetMarketForTranslation(String(bet.market ?? ""), String(bet.market_type ?? bet.marketType ?? "")) || ""
  ).toLowerCase();
  const hay = `${cn} ${blob}`;

  return (
    /\bmoneyline\b/.test(hay) ||
    /\b1x2\b/.test(hay) ||
    /\bspread\b/.test(hay) ||
    /\bhandicap\b/.test(hay) ||
    /\brun\s*line\b/.test(hay) ||
    /\bpuck\s*line\b/.test(hay) ||
    /\btotal/.test(hay) ||
    /\btotals\b/.test(hay) ||
    /\bover\/under\b/.test(hay) ||
    /\bteam\s*total/.test(hay) ||
    /\bmatch\s+winner\b/.test(hay) ||
    /\bdouble\s+chance\b/.test(hay)
  );
}

/**
 * Period token for end of single-line label: market identifiers, Sharp fields, translated label.
 * Esports: suffix only when HT/2H/FT is explicit in user-facing market text (no default FT).
 */
function extractValueBetPeriodSuffix(bet, marketLabel, rawMarket) {
  const rawSport = bet?.sport ?? bet?.sportKey ?? bet?.sportName ?? bet?.category ?? bet?.gameType ?? "";
  const league = bet?.league ?? "";
  const match = bet?.match ?? bet?.game ?? "";
  const tabSport = classifySportForAnalysisTab(rawSport, { league, match });
  const isEsports = tabSport === "esports";

  const labelStr = String(marketLabel ?? "");
  const rawStr = String(rawMarket ?? "");

  const textUserFacing = toShortPeriodTokenFromTextLabel(
    extractMarketPeriodSuffix(labelStr) || extractMarketPeriodSuffix(rawStr)
  );

  const identifierBlob = [
    labelStr,
    rawStr,
    bet?.market,
    bet?.market_type,
    bet?.marketType,
    bet?.market_id,
    bet?.marketId,
  ]
    .map((x) => String(x ?? ""))
    .join(" ");

  const textAllSources = toShortPeriodTokenFromTextLabel(extractMarketPeriodSuffix(identifierBlob));

  if (isEsports) return textUserFacing;

  const live = valueBetIsLiveForPeriodSuffix(bet);
  const liveBlocked = live && liveSportSpecificPeriodBlocksHalfFt(bet);

  let textToken = "";
  if (liveBlocked) {
    textToken = textUserFacing;
  } else if (textAllSources && textUserFacing) {
    textToken = mergeCompatiblePeriodSuffixes(textAllSources, textUserFacing) || textUserFacing;
  } else {
    textToken = textAllSources || textUserFacing;
  }

  let structToken = inferStructuredPeriodSuffix(bet);
  if (liveBlocked && !textUserFacing) structToken = "";

  let out = "";
  if (textToken && structToken) out = mergeCompatiblePeriodSuffixes(textToken, structToken);
  else out = textToken || structToken || "";

  if (!out && !live && shouldOfferPrematchDefaultFt(bet, identifierBlob)) out = "FT";

  return out;
}

/* ------------------------------------------------ */

/**
 * Master commercial-safety flag for the Value tab.
 *
 * `false` (default — public app):
 *   The public Value list renders ONLY Raw Verified / raw_ready / active rows
 *   that pass `isPublicRawReadyRow`. Source Verified, Grace and any row that
 *   carries a non-empty `rawFailureReason` are hidden entirely. This protects
 *   public users from clicking a row whose odds/line no longer match the
 *   sportsbook UI.
 *
 * `true` (admin/debug only):
 *   Source Verified rows are surfaced under a separate "Watchlist - Cần kiểm
 *   nhà cái" section with NO "Vào cược" button and a "Không phải kèo vào cược"
 *   caption. Grace/Recheck and other non-Ready rows still stay hidden. The
 *   Raw Ready list is rendered above the watchlist and the two are never
 *   intermixed.
 *
 * Hard rules implemented around this flag:
 *   - Source Verified rows must NEVER be marked "Ready".
 *   - Source Verified rows must NEVER be rendered as a normal public bet row.
 *   - Grace/Recheck rows must NEVER be rendered for the public user.
 *   - "Vào cược" exists only on Raw Verified / raw_ready cards and must run
 *     a preflight check before opening the deep link.
 */
/**
 * Strict public-safe filter: a row passes only if it is a Raw Verified
 * opportunity that is currently safe to bet right now. Public rendering
 * code MUST gate every actionable row through this function.
 *
 * Rejection cases (non-exhaustive):
 *   • offerVerification.status !== "verified"
 *   • isRawOfferVerified !== true
 *   • betReadiness !== "raw_ready"
 *   • lifecycleStatus !== "active"
 *   • Any non-empty `rawFailureReason` (covers stale_raw, no_selection,
 *     no_same_book, raw_fetch_failed, odds_conflict, team_total_side_ambiguous,
 *     source_check_required, grace_check_required, etc.)
 *   • Live row with offerVerification.rawAgeSeconds > 5
 *   • Prematch row with offerVerification.rawAgeSeconds > 60
 *   • Team-total-like market without offerVerification.teamTotalSideVerified === true
 *   • Missing or non-finite odds, missing selection, missing market
 *   • Line provided but not parseable as a finite number
 */
function isPublicRawReadyRow(bet) {
  if (!bet || typeof bet !== "object") return false;
  const ov =
    bet.offerVerification && typeof bet.offerVerification === "object"
      ? bet.offerVerification
      : null;
  if (!ov) return false;
  if (String(ov.status || "").trim() !== "verified") return false;
  if (bet.isRawOfferVerified !== true) return false;
  if (String(bet.betReadiness || "").trim() !== "raw_ready") return false;
  if (String(bet.lifecycleStatus || "").trim().toLowerCase() !== "active") return false;

  // Any explicit raw failure reason (even alongside a "verified" status) is
  // disqualifying — the row is too risky for public actionable display.
  const rfr = String(bet.rawFailureReason ?? ov.rawFailureReason ?? "").trim();
  if (rfr) return false;

  // Offered odds must parse to a real decimal price > 1.
  const decOdds = valueBetOfferedOddsToDecimal(bet);
  if (decOdds == null || !Number.isFinite(decOdds) || decOdds <= 1) return false;

  // Selection and market identifiers must be present.
  const selectionRaw =
    bet.selection ??
    bet.display_selection ??
    bet.displaySelection ??
    bet.pick ??
    bet.outcome ??
    "";
  if (typeof selectionRaw !== "string" && typeof selectionRaw !== "number") return false;
  if (String(selectionRaw).trim() === "") return false;
  const marketRaw = bet.market ?? bet.market_type ?? bet.marketType ?? "";
  if (String(marketRaw).trim() === "") return false;

  // When a line is supplied, it must parse to a finite number; missing line
  // (moneyline / 1x2) is allowed.
  const lineRaw =
    bet.line ??
    bet?.market?.line ??
    bet?.selection?.line ??
    bet.point ??
    bet.handicap ??
    bet.total ??
    "";
  if (lineRaw !== "" && lineRaw !== null && lineRaw !== undefined) {
    const ln = Number(lineRaw);
    if (Number.isNaN(ln) || !Number.isFinite(ln)) return false;
  }

  // Raw freshness gates (live ≤5s, prematch ≤60s — matching backend preflight).
  const isLive = isLiveValueBetRow(bet);
  if (typeof ov.rawAgeSeconds === "number" && Number.isFinite(ov.rawAgeSeconds)) {
    if (ov.rawAgeSeconds < 0) return false;
    const maxAge = isLive ? 5 : 60;
    if (ov.rawAgeSeconds > maxAge) return false;
  }

  // Team-total markets are only safe when the side itself was independently
  // verified by the backend.
  const safety = getValueTeamTotalSideSafety(bet);
  if (safety.isTeamTotalLike) {
    if (ov.teamTotalSideVerified !== true) return false;
    if (!safety.sideTrusted) return false;
    if (!safety.isRawTeamTotalSafe) return false;
  }

  return true;
}

/**
 * Public-safe source-check filter. This is intentionally narrower than
 * general "check" states: only backend-marked Source Verified rows pass.
 * Grace/recheck states stay hidden from the public Value tab.
 */
function isPublicSourceCheckRow(bet) {
  if (!bet || typeof bet !== "object") return false;
  const ov =
    bet.offerVerification && typeof bet.offerVerification === "object"
      ? bet.offerVerification
      : null;
  if (!ov) return false;
  if (String(ov.status || "").trim() !== "source_verified") return false;
  if (String(bet.lifecycleStatus || "").trim().toLowerCase() !== "active") return false;
  return true;
}

function renderMatchWithColoredAt(match) {
  const raw = String(match ?? "");
  if (!raw) return raw;
  const parts = raw.split(" @ ");
  if (parts.length !== 2) return raw;
  return (
    <>
      <span>{parts[0]}</span>
      <span className="vb-at-sep"> @ </span>
      <span>{parts[1]}</span>
    </>
  );
}

function getStatusLabel(b, t, lang, timeZone) {
  return getAnalysisStatusLabel(b, t, lang, "—", timeZone);
}

/**
 * Real/canonical sport for a bet, as determined by the single shared
 * classifier. Returns "football" | "tennis" | "basketball" | "esports" |
 * "unsupported". Use this (not `getBetSport`) for any presentation/semantic
 * decision that must know the bet's TRUE sport (e.g. esports-specific
 * formatting) — NOT for Value tab routing/filtering.
 */
function getBetCanonicalSport(b) {
  const raw = b?.sport ?? b?.sportKey ?? b?.sportName ?? b?.category ?? b?.gameType ?? "";
  const match = b?.match ?? b?.game ?? b?.eventName ?? "";
  return classifySportForAnalysisTab(raw, { league: b?.league, match });
}

/**
 * Visible Value tab for a bet. This is ONLY for tab routing/filtering/badge
 * counts. Canonical "unsupported" sports (NFL, NHL, MLB, cricket, table
 * tennis, unknown, etc.) are routed into the Esports tab as a fallback
 * bucket — they remain their real sport for presentation purposes, see
 * `getBetCanonicalSport`.
 */
function getBetSport(b) {
  const canonicalSport = getBetCanonicalSport(b);
  return canonicalSport === "unsupported" ? "esports" : canonicalSport;
}

function getEsportsLeagueLabel(b, lang) {
  const game = String(b?.game ?? b?.gameTitle ?? b?.title ?? "").trim();
  const league = translateLeagueDisplay(String(b?.league ?? "").trim(), lang);

  if (!game) return league;
  if (!league) return game;

  return `${league} · ${game}`;
}

function getEsportsMarketLabel(b, lang) {
  const rawMarket = pickBestRawMarketSource(b);
  const canonical = canonicalValueBetMarketForTranslation(b?.market, b?.market_type ?? b?.marketType);
  const market =
    translateMarketDisplayWithPeriodSplit(rawMarket, lang) ||
    translateMarketDisplay(canonical, lang) ||
    canonical ||
    rawMarket;
  const map = String(b?.map ?? b?.mapName ?? "").trim();

  if (!map) return market;
  if (!market) return map;
  return `${market} · ${map}`;
}

function buildSafeFallbackValueBetLabel(b, language) {
  const rawMarketBest = pickBestRawMarketSource(b);
  const canonicalKey = canonicalValueBetMarketForTranslation(b?.market, b?.market_type ?? b?.marketType);

  const marketLabel =
    translateMarketDisplayWithPeriodSplit(rawMarketBest, language) ||
    translateMarketDisplay(canonicalKey, language) ||
    translateMarketDisplay(rawMarketBest, language) ||
    canonicalKey ||
    rawMarketBest ||
    "";

  const periodSuffix = extractValueBetPeriodSuffix(b, marketLabel, rawMarketBest);
  const linePart = pickLineFromValueBet(b);
  const lineStr = isMeaningfulLineForCard(linePart) ? String(linePart).trim() : "";

  const base = String(marketLabel ?? "").trim();
  let withLine = base;
  if (base && lineStr && !selectionAlreadyContainsLine(base, lineStr)) {
    withLine = `${base} · ${lineStr}`.replace(/\s+/g, " ").trim();
  }

  const selectionLabel = buildValueBetTeamResolvedSelectionLabel(b, language);
  if (!withLine) {
    if (selectionLabel) withLine = String(selectionLabel).trim();
    else if (lineStr) withLine = lineStr;
  }

  const out = appendMarketPeriodSuffixIfMissing(withLine, periodSuffix);
  if (/^market\b/i.test(String(out ?? "").trim())) {
    // Absolute last resort only (avoid "Market X" when semantic fields exist).
    if (base || selectionLabel || rawMarketBest || canonicalKey || b?.market_type || b?.marketType) {
      const fallback = appendMarketPeriodSuffixIfMissing(base || String(selectionLabel ?? "").trim() || "", periodSuffix);
      if (fallback && !/^market\b/i.test(fallback)) return fallback;
    }
  }
  return out;
}

function buildSingleLineValueBetLabel(b, language) {
  const semanticCore = buildCanonicalValueBetDisplayLabel(b, language);
  if (semanticCore) {
    const rawMarketBest = pickBestRawMarketSource(b);
    const canonicalKey = canonicalValueBetMarketForTranslation(b?.market, b?.market_type ?? b?.marketType);
    const marketLabelForPeriod =
      translateMarketDisplayWithPeriodSplit(rawMarketBest, language) ||
      translateMarketDisplay(canonicalKey, language) ||
      translateMarketDisplay(rawMarketBest, language) ||
      canonicalKey ||
      rawMarketBest ||
      "";
    const periodSuffix = extractValueBetPeriodSuffix(b, marketLabelForPeriod, rawMarketBest);
    return appendMarketPeriodSuffixIfMissing(semanticCore, periodSuffix);
  }

  const rawMarketBest = pickBestRawMarketSource(b);
  const canonicalKey = canonicalValueBetMarketForTranslation(b?.market, b?.market_type ?? b?.marketType);
  const marketLabel =
    translateMarketDisplayWithPeriodSplit(rawMarketBest, language) ||
    translateMarketDisplay(canonicalKey, language) ||
    translateMarketDisplay(rawMarketBest, language) ||
    canonicalKey ||
    rawMarketBest ||
    "";
  const periodSuffix = extractValueBetPeriodSuffix(b, marketLabel, rawMarketBest);

  const selectionLabelRaw = buildValueBetTeamResolvedSelectionLabel(b, language);
  const selectionLabel = String(selectionLabelRaw ?? "").trim();
  const selectionLabelLower = selectionLabel.toLowerCase();
  const linePart = pickLineFromValueBet(b);
  const lineStr = isMeaningfulLineForCard(linePart) ? String(linePart).trim() : "";

  const home = String(b?.home_team ?? b?.homeTeam ?? "").trim();
  const away = String(b?.away_team ?? b?.awayTeam ?? "").trim();

  const familyHay = `${canonicalKey || ""} ${rawMarketBest || ""} ${marketLabel || ""} ${String(b?.market_type ?? b?.marketType ?? "")}`.toLowerCase();
  const isTotalsLike =
    /\b(total|totals|team\s*total|over\/under|over\s*under|ou)\b/.test(familyHay) ||
    /\b(over|under)\b/.test(selectionLabelLower);
  const isHandicapLike = /\b(handicap|spread|run\s*line|puck\s*line)\b/.test(familyHay);
  const isWinnerLike =
    /\b(moneyline|match\s*winner|winner|1x2|double\s*chance)\b/.test(familyHay) ||
    /\b(draw)\b/.test(selectionLabelLower);

  const marketClean = String(marketLabel ?? "").trim();
  const marketLower = marketClean.toLowerCase();

  const containsLine = (txt) => (txt && lineStr ? selectionAlreadyContainsLine(txt, lineStr) : false);
  const safeJoin = (a, b2, sep = " ") => {
    const aa = String(a ?? "").trim();
    const bb = String(b2 ?? "").trim();
    if (!aa) return bb;
    if (!bb) return aa;
    if (aa.toLowerCase() === bb.toLowerCase()) return aa;
    return `${aa}${sep}${bb}`.replace(/\s+/g, " ").trim();
  };

  let built = "";

  if (isTotalsLike) {
    if (marketClean) {
      built = marketClean;
      if (selectionLabel && !built.toLowerCase().includes(selectionLabelLower)) {
        built = safeJoin(built, selectionLabel, " ");
      }
      if (lineStr && !containsLine(built)) built = safeJoin(built, lineStr, " ");
    } else if (selectionLabel) {
      built = selectionLabel;
      if (lineStr && !containsLine(built)) built = safeJoin(built, lineStr, " ");
    }
  } else if (isHandicapLike) {
    if (selectionLabel) {
      built = selectionLabel;
      if (marketClean && !built.toLowerCase().includes(marketLower) && !marketLower.includes(built.toLowerCase())) {
        built = safeJoin(marketClean, built, " ");
      }
      if (lineStr && !containsLine(built) && (/^[+-]/.test(lineStr) || /[+-]\s*\d/.test(lineStr))) {
        built = safeJoin(built, lineStr, " ");
      } else if (lineStr && !containsLine(built) && /^[+-]/.test(selectionLabel) === false && /[+-]\s*\d/.test(selectionLabel) === false) {
        // Only append raw line when selection label doesn't already encode it.
        built = safeJoin(built, lineStr, " ");
      }
    } else if (marketClean) {
      built = marketClean;
      if (lineStr && !containsLine(built)) built = safeJoin(built, lineStr, " ");
    }
  } else if (isWinnerLike) {
    if (selectionLabel) {
      built = selectionLabel;
      const needMarket =
        marketClean &&
        marketClean.length >= 6 &&
        !built.toLowerCase().includes(marketLower) &&
        !marketLower.includes(built.toLowerCase());
      if (needMarket && !/^(home|away)$/i.test(selectionLabelLower)) {
        built = safeJoin(built, marketClean, " ");
      }
    } else if (marketClean) {
      built = marketClean;
    }
  }

  if (!built) {
    if (marketClean && selectionLabel) {
      built = safeJoin(marketClean, selectionLabel, " · ");
    } else if (selectionLabel) {
      built = selectionLabel;
    } else if (marketClean) {
      built = marketClean;
    }

    if (built && lineStr && !containsLine(built) && !selectionLabelLower.includes(lineStr.toLowerCase())) {
      built = safeJoin(built, lineStr, " ");
    }
  }

  if (home && built && built.toLowerCase() === "home") built = home;
  if (away && built && built.toLowerCase() === "away") built = away;

  built = appendMarketPeriodSuffixIfMissing(built, periodSuffix);

  if (built && !/^market\b/i.test(built)) return built;
  return buildSafeFallbackValueBetLabel(b, language);
}

function buildNormalSportMarketFamilyLabel(b, language) {
  const rawMarketBest = pickBestRawMarketSource(b);
  const canonicalKey = canonicalValueBetMarketForTranslation(b?.market, b?.market_type ?? b?.marketType);

  const translatedFromRaw =
    translateMarketDisplayWithPeriodSplit(rawMarketBest, language) ||
    translateMarketDisplay(rawMarketBest, language) ||
    "";

  const translatedFromCanonical = canonicalKey ? translateMarketDisplay(canonicalKey, language) || "" : "";

  let marketLabel = translatedFromRaw || translatedFromCanonical || canonicalKey || rawMarketBest || "";

  // If translation collapses into a selection-like fragment (e.g. "Over", "Under 2.5"),
  // prefer the canonical market family label when available.
  if (marketStringLooksLikePlainSelectionFragment(marketLabel) && (translatedFromCanonical || canonicalKey)) {
    marketLabel = translatedFromCanonical || canonicalKey || marketLabel;
  }

  if (!String(marketLabel ?? "").trim()) return "";

  const periodSuffix = extractValueBetPeriodSuffix(b, marketLabel, rawMarketBest);
  return appendMarketPeriodSuffixIfMissing(marketLabel, periodSuffix);
}

function formatPreflightFailMessage(reason) {
  const r = String(reason || "").toLowerCase();
  if (r === "odds_changed") return "Odds đã thay đổi, kiểm tra lại nhà cái";
  if (r === "line_changed") return "Line đã thay đổi, kiểm tra lại nhà cái";
  if (r === "selection_changed") return "Selection đã thay đổi, kiểm tra lại nhà cái";
  if (r === "team_total_side_ambiguous") return "Không xác minh được đội, kiểm tra lại nhà cái";
  if (r === "stale_raw") return "Raw odds quá cũ, kiểm tra lại nhà cái";
  if (r === "raw_fetch_failed") return "Không lấy được raw odds, kiểm tra lại nhà cái";
  if (r === "row_not_raw_ready") return "Cơ hội không còn Raw Ready";
  if (r === "row_not_found") return "Không tìm thấy cơ hội này";
  if (r === "network_error") return "Lỗi mạng khi kiểm tra, thử lại sau";
  if (
    r === "row_view_mismatch" ||
    r === "missing_bet" ||
    r === "missing_response" ||
    r === "sportsbook_mismatch" ||
    r === "market_mismatch" ||
    r === "selection_mismatch" ||
    r === "line_mismatch" ||
    r === "line_missing" ||
    r === "odds_mismatch" ||
    r === "row_odds_missing" ||
    r === "matched_odds_missing" ||
    r === "missing_matched_raw" ||
    r === "server_gate_failed"
  ) {
    return "Kèo đã đổi / cần kiểm tra lại";
  }
  return "Odds/line đã thay đổi, kiểm tra lại nhà cái";
}

const ValueCard = React.memo(function ValueCard({
  bet,
  lang,
  t,
  timeZone,
  isFlashing,
  isLeaving,
  splits,
  isCopied,
  onCopy,
  isDebugWatchlist,
  gameState,
  gameStateExtras,
  gameStatePeriodLabel,
  oddsFormat = "hk",
}) {
  const [showDetail, setShowDetail] = React.useState(false);
  const oddsLabel = t?.common?.oddsLabel || t?.common?.odds || "Odds";
  const trueOddsLabel = t?.common?.trueOddsLabel || t?.value?.trueOdds || "True Odds";
  const statusInfo = getStatusLabel(bet, t, lang, timeZone);
  const displayTimeValue = computeValueBetCardDisplayTimeValue(statusInfo, bet, lang, timeZone);
  // ── Game state helpers for live soccer ──
  const hasLiveGameState = Boolean(statusInfo.isLive && gameState && hasGameStateData(gameState));
  const gsPeriodClock = hasLiveGameState ? getGameStatePeriodClock(gameState) : "";
  const gsTeams       = hasLiveGameState ? getGameStateTeams(bet, gameState) : null;
  const gsScore       = hasLiveGameState ? getGameStateScore(gameState) : null;
  const gsPossession  = hasLiveGameState ? getGameStatePossessionSide(gameState) : "";
  const gsStatRows    = hasLiveGameState ? getGameStateStatRows(gameState) : [];
  const isEsports = getBetCanonicalSport(bet) === "esports";
  const esportsLeague = getEsportsLeagueLabel(bet, lang);
  const esportsMarket = getEsportsMarketLabel(bet, lang);
  const offerBook = getOfferedValueBookName(bet);
  const verifyBadgeMeta = getOfferVerificationBadgeMeta(bet);
  const ovInfoDisplay = bet?.offerVerification && typeof bet.offerVerification === "object" ? bet.offerVerification : null;
  const ovStatusDisplay = ovInfoDisplay ? String(ovInfoDisplay.status || "").trim() : "";
  const isVerifiedOrSourceDisplay =
    ovStatusDisplay === "verified" || ovStatusDisplay === "source_verified";
  const canonicalDisplay = buildValueCanonicalDisplayEx(bet, lang);
  // Verified raw from preflight (offerVerification.matched* fields). When
  // present, downstream display helpers prefer these over opportunity-feed
  // fields so the card shows the exact sportsbook offer that was matched.
  const valueDisplayVerifiedRaw = getValueRowVerifiedRaw(bet);
  const valueRowMarketFamily = getValueRowMarketFamily(bet, valueDisplayVerifiedRaw);
  const baseSelectionLabel = isEsports
    ? buildValueBetTeamResolvedSelectionLabel(bet, lang)
    : buildValueBetSelectionRowLabel(bet, lang);
  const displayLeague = translateLeagueDisplay(bet.league, lang);
  const mRaw = String(bet.market ?? "").trim();
  const isInvalidMarketRow = (v) => {
    const s = String(v ?? "").trim();
    if (!s) return true;
    const scopeOnly = /^(ft|ht|2h|q[1-4]|10m|15m|30m|60m)$/i.test(s) || /^set\s*[12]$/i.test(s);
    if (scopeOnly) return true;
    const segs = s.split("·").map((x) => x.trim()).filter(Boolean);
    if (!segs.length) return true;
    const first = segs[0] || "";
    const firstIsScope = /^(ft|ht|2h|q[1-4]|10m|15m|30m|60m)$/i.test(first) || /^set\s*[12]$/i.test(first);
    if (firstIsScope) return true;
    return false;
  };

  const isLegacySelectionFirstMarketRow = (b, legacy, language) => {
    const s = String(legacy ?? "").trim();
    if (!s) return false;
    const low = s.toLowerCase();
    const home = String(b?.home_team ?? b?.homeTeam ?? "").trim();
    const away = String(b?.away_team ?? b?.awayTeam ?? "").trim();
    const normTok = s.replace(/\s+/g, "_").toLowerCase();
    if (/^(home|away)_(over|under)$/i.test(normTok)) return true;
    if (/\b(home|away)_(over|under)\b/i.test(s)) return true;
    const wOver = String(
      translateSelectionDisplay("over", language) || mapFixtureBetToLabel("over", language) || "Over"
    ).toLowerCase();
    const wUnder = String(
      translateSelectionDisplay("under", language) || mapFixtureBetToLabel("under", language) || "Under"
    ).toLowerCase();
    const startsOu =
      low === wOver ||
      low.startsWith(`${wOver} `) ||
      low === wUnder ||
      low.startsWith(`${wUnder} `) ||
      /^over\b/i.test(low) ||
      /^under\b/i.test(low);
    if (startsOu) return true;
    if (home && low.startsWith(home.toLowerCase())) return true;
    if (away && low.startsWith(away.toLowerCase())) return true;
    return false;
  };

  const marketRowFromHelper = buildValueBetMarketRowLabel(bet, lang);
  const marketRowFromFamilyFallback = buildNormalSportMarketFamilyLabel(bet, lang);
  const marketRowFromLegacyFallback = buildSingleLineValueBetLabel(bet, lang);
  const marketRowFromTypeOnly = buildValueBetMarketFamilyOnlyRowLabel(bet, lang);
  const legacyIsSelectionFirst = isLegacySelectionFirstMarketRow(bet, marketRowFromLegacyFallback, lang);

  let displayMarket = isEsports
    ? esportsMarket
    : !isInvalidMarketRow(marketRowFromTypeOnly)
      ? marketRowFromTypeOnly
      : !isInvalidMarketRow(marketRowFromHelper)
        ? marketRowFromHelper
        : !isInvalidMarketRow(marketRowFromFamilyFallback)
          ? marketRowFromFamilyFallback
          : !legacyIsSelectionFirst && String(marketRowFromLegacyFallback ?? "").trim()
            ? marketRowFromLegacyFallback
            : marketRowFromLegacyFallback;

  // Legacy translated chain remains available as fallback.
  const semanticMarketLabel = buildValueSemanticMarketDisplay(bet, lang);
  const displayMarketTrim = String(displayMarket ?? "").trim();
  if (!displayMarketTrim || looksLikeRawMarketKey(displayMarketTrim)) {
    if (semanticMarketLabel && !looksLikeRawMarketKey(semanticMarketLabel)) {
      displayMarket = semanticMarketLabel;
    } else if (displayMarketTrim && looksLikeRawMarketKey(displayMarketTrim)) {
      displayMarket = humanizeRawMarketLabel(displayMarketTrim);
    }
  }

  // Generic Value display override (Task C): when the row resolves to a
  // structured Value family (handicap / total / team_total / moneyline),
  // prefer the analysisTranslate Value-family label so the card header
  // consistently reads "Cược Chấp · FT", "Tài/Xỉu · HT", "Kèo Thắng" etc.
  // The line never appears in the header — it lives on the selection row.
  const supportsStructuredValueFamilyDisplay =
    valueRowMarketFamily === "spread" ||
    valueRowMarketFamily === "total" ||
    valueRowMarketFamily === "team_total" ||
    valueRowMarketFamily === "moneyline";
  if (supportsStructuredValueFamilyDisplay) {
    const familyKeyForHeader = String(
      bet?.market_type || bet?.marketType || bet?.market || ""
    ).trim();
    const familyTranslated = translateValueMarketFamilyKeySafe(familyKeyForHeader, lang);
    const helperMarketLabel = buildValueDisplayMarketLabelEx(bet, lang, valueDisplayVerifiedRaw);
    const candidateMarket = familyTranslated
      ? (() => {
          const scope = String(helperMarketLabel || "").split("·").map((p) => p.trim());
          const scopeSuffix = scope.length > 1 ? scope.slice(1).join(" · ") : "";
          return scopeSuffix ? `${familyTranslated} · ${scopeSuffix}` : familyTranslated;
        })()
      : helperMarketLabel;
    if (candidateMarket && !looksLikeRawMarketKey(candidateMarket)) {
      const currentTrim = String(displayMarket ?? "").trim();
      // Override when the existing label is empty, looks like a raw key,
      // or is missing the structured Value family token.
      const candidateLow = candidateMarket.toLowerCase();
      const currentLow = currentTrim.toLowerCase();
      if (
        !currentTrim ||
        looksLikeRawMarketKey(currentTrim) ||
        (currentLow !== candidateLow && !currentLow.includes(candidateLow.split(" · ")[0]))
      ) {
        displayMarket = candidateMarket;
      }
    }
  }
  const canonicalMarketLabel = String(canonicalDisplay?.marketLabel || "").trim();
  const canonicalSelectionLabel = String(canonicalDisplay?.selectionLabel || "").trim();
  const canonicalTeamTotalResolved = canonicalDisplay?.teamTotalResolved || null;
  const fallbackTeamTotalSelectionLabel = String(lang || "").toLowerCase().startsWith("vi")
    ? "Cần kiểm đội"
    : "Check team";
  const canonicalSelectionContainsResolvedTeam =
    canonicalTeamTotalResolved &&
    canonicalTeamTotalResolved.ok === true &&
    String(canonicalTeamTotalResolved.teamName || "").trim() &&
    canonicalSelectionLabel
      .toLowerCase()
      .includes(String(canonicalTeamTotalResolved.teamName || "").trim().toLowerCase());
  if (isVerifiedOrSourceDisplay && canonicalMarketLabel) {
    displayMarket = canonicalMarketLabel;
  }
  const teamTotalSafety = getValueTeamTotalSideSafety(bet, displayMarket);
  const teamTotalDisplayResolution = teamTotalSafety.isTeamTotalLike
    ? resolveValueTeamTotalDisplaySideEx(bet, valueDisplayVerifiedRaw)
    : null;
  const teamTotalDisplayResolvedOk = Boolean(
    teamTotalSafety.isTeamTotalLike &&
      teamTotalDisplayResolution &&
      teamTotalDisplayResolution.ok === true &&
      String(teamTotalDisplayResolution.teamName || "").trim()
  );
  const teamTotalNeedsSideWarning = teamTotalSafety.isTeamTotalLike && !teamTotalDisplayResolvedOk;

  // Defensive: when team-total side is ambiguous, the main market row
  // must also be free of any leading/trailing home/away team name. The
  // helper labels normally avoid leaking the team, but we scrub here as
  // a last line of defense so that the UI never displays a specific
  // team for an unverified team-total row.
  if (teamTotalNeedsSideWarning) {
    const scrubLeadingTeam = (label) => {
      const s = String(label ?? "").trim();
      if (!s) return s;
      const home = String(bet?.home_team ?? bet?.homeTeam ?? "").trim();
      const away = String(bet?.away_team ?? bet?.awayTeam ?? "").trim();
      const tryStrip = (name) => {
        if (!name) return null;
        const lo = s.toLowerCase();
        const ln = String(name).toLowerCase();
        if (lo === ln) return "";
        if (lo.startsWith(`${ln} `)) return s.slice(name.length).trim();
        if (lo.endsWith(` ${ln}`)) return s.slice(0, s.length - name.length).trim();
        return null;
      };
      const homeStripped = tryStrip(home);
      if (homeStripped !== null) return homeStripped;
      const awayStripped = tryStrip(away);
      if (awayStripped !== null) return awayStripped;
      return s;
    };
    const scrubbed = scrubLeadingTeam(displayMarket);
    if (scrubbed) displayMarket = scrubbed;
  }
  // Legacy translated selection path remains available as fallback.
  const semanticSelectionLabel = buildValueSemanticSelectionDisplay(bet, lang);
  const baseSelectionTrim = String(baseSelectionLabel ?? "").trim();
  const baseSelectionIsClean = baseSelectionTrim && !looksLikeRawMarketKey(baseSelectionTrim);
  const safeTeamTotalSelectionLabel = teamTotalSafety.isTeamTotalLike
    ? buildSafeTeamTotalSelectionLabel(bet, lang, teamTotalSafety, "display")
    : "";
  const resolvedTeamNameForDisplay = String(teamTotalDisplayResolution?.teamName || "").trim();
  const canonicalSelectionContainsDisplayResolvedTeam =
    Boolean(teamTotalDisplayResolvedOk) &&
    Boolean(canonicalSelectionLabel) &&
    canonicalSelectionLabel.toLowerCase().includes(resolvedTeamNameForDisplay.toLowerCase());
  const buildTeamTotalResolvedDisplayLabel = () => {
    if (!teamTotalDisplayResolvedOk) return "";
    const direction = String(teamTotalDisplayResolution?.direction || "").toUpperCase();
    const isVi = String(lang || "").toLowerCase().startsWith("vi");
    const directionLabel =
      direction === "UNDER"
        ? isVi
          ? "Xỉu"
          : "Under"
        : direction === "OVER"
          ? isVi
            ? "Tài"
            : "Over"
          : "";
    const resolvedLine = String(teamTotalDisplayResolution?.line || "").trim();
    const fallbackLine = String(
      formatValueRowDisplayLine(bet, valueDisplayVerifiedRaw) || buildValueDisplayLineText(bet) || pickLineFromValueBet(bet) || ""
    ).trim();
    const lineForDisplay = resolvedLine || fallbackLine;
    return [resolvedTeamNameForDisplay, directionLabel, lineForDisplay].filter(Boolean).join(" ").trim();
  };
  let selectionLabel = teamTotalSafety.isTeamTotalLike
    ? safeTeamTotalSelectionLabel || fallbackTeamTotalSelectionLabel
    : baseSelectionIsClean
      ? baseSelectionLabel
      : semanticSelectionLabel && !looksLikeRawMarketKey(semanticSelectionLabel)
        ? semanticSelectionLabel
        : baseSelectionLabel;

  // Generic Value selection override (Task D): for structured handicap /
  // total / moneyline families the helper enforces:
  //   • spread  → "<Team> <signed line>"
  //   • total   → "<Tài|Xỉu> <unsigned line>"
  //   • moneyline → "<Team>" (or Hòa / Draw); never appends a line.
  // Team-total flows continue to use the safety-aware label above so the
  // "Cần kiểm đội" path is preserved untouched.
  if (
    !teamTotalSafety.isTeamTotalLike &&
    (valueRowMarketFamily === "spread" ||
      valueRowMarketFamily === "total" ||
      valueRowMarketFamily === "moneyline")
  ) {
    const valueDisplaySelection = buildValueDisplaySelectionLabelEx(
      bet,
      valueDisplayVerifiedRaw,
      lang
    );
    if (valueDisplaySelection && !looksLikeRawMarketKey(valueDisplaySelection)) {
      const currentSel = String(selectionLabel ?? "").trim();
      const lineForFamily = formatValueRowDisplayLine(bet, valueDisplayVerifiedRaw);
      const familyExpectsLine =
        valueRowMarketFamily === "spread" || valueRowMarketFamily === "total";
      const selectionMissingLine =
        familyExpectsLine && !!lineForFamily && !selectionAlreadyContainsLine(currentSel, lineForFamily);
      const selectionHasStaleLine =
        valueRowMarketFamily === "moneyline" &&
        /[+-]?\d+(?:\.\d+)?/.test(currentSel) &&
        !/[+-]?\d+(?:\.\d+)?/.test(valueDisplaySelection);
      if (
        !currentSel ||
        looksLikeRawMarketKey(currentSel) ||
        selectionMissingLine ||
        selectionHasStaleLine
      ) {
        selectionLabel = valueDisplaySelection;
      }
    }
  }
  if (teamTotalSafety.isTeamTotalLike) {
    if (canonicalSelectionContainsResolvedTeam) {
      selectionLabel = canonicalSelectionLabel;
    } else if (canonicalSelectionContainsDisplayResolvedTeam) {
      selectionLabel = canonicalSelectionLabel;
    } else if (teamTotalDisplayResolvedOk) {
      selectionLabel = buildTeamTotalResolvedDisplayLabel() || safeTeamTotalSelectionLabel || fallbackTeamTotalSelectionLabel;
    } else if (safeTeamTotalSelectionLabel) {
      selectionLabel = safeTeamTotalSelectionLabel;
    } else {
      selectionLabel = fallbackTeamTotalSelectionLabel;
    }
  } else if (isVerifiedOrSourceDisplay && canonicalSelectionLabel) {
    selectionLabel = canonicalSelectionLabel;
  }
  const selectionLabelBeforePlayerPropLinePatch = String(selectionLabel || "").trim();
  const isPlayerPropDisplay = isValuePlayerPropLikeRow(bet);
  const playerPropLine =
    formatValueRowDisplayLine(bet, valueDisplayVerifiedRaw) || pickLineFromValueBet(bet);
  if (
    isPlayerPropDisplay &&
    isMeaningfulLineForCard(playerPropLine) &&
    selectionLabelBeforePlayerPropLinePatch &&
    !selectionAlreadyContainsLine(selectionLabelBeforePlayerPropLinePatch, playerPropLine) &&
    isDirectionLikeSelectionLabel(selectionLabelBeforePlayerPropLinePatch)
  ) {
    selectionLabel = `${selectionLabelBeforePlayerPropLinePatch} ${playerPropLine}`.trim();
  }
  const copySelectionLabel = teamTotalSafety.isTeamTotalLike
    ? ((teamTotalDisplayResolvedOk &&
        String(selectionLabel || "").toLowerCase().includes(resolvedTeamNameForDisplay.toLowerCase()))
        ? selectionLabel
        : canonicalSelectionContainsResolvedTeam
        ? canonicalSelectionLabel
        : buildSafeTeamTotalSelectionLabel(bet, lang, teamTotalSafety, "copy") ||
          fallbackTeamTotalSelectionLabel)
    : selectionLabel;
  const showSelectionRow = (() => {
    // Ambiguous team total rows must always show the neutral selection
    // label and the "Cần kiểm đội" warning chip, even if the market row
    // already carries the same words by coincidence.
    if (teamTotalNeedsSideWarning) return true;
    if (isEsports) {
      const selRawEs = String(bet.selection ?? bet.pick ?? bet.outcome ?? "").trim();
      if (!selRawEs) return false;
      return selectionLabel ? selRawEs.toLowerCase() !== mRaw.toLowerCase() : false;
    }
    if (!selectionLabel) return false;

    const main = String(displayMarket ?? "").trim();
    const sel = String(selectionLabel ?? "").trim();
    if (!sel) return false;
    if (!main) return true;

    return main.toLowerCase() !== sel.toLowerCase();
  })();

  const stableKey = getValueRenderIdentityKey(bet) || `__row_${bet.id || ""}`;
  const ovInfo = ovInfoDisplay;
  const ovStatus = ovStatusDisplay;
  const verificationState = getValueBetCommercialState(bet);
  const isGraceRow = ovStatus === "grace_retained";
  const isSourceRow = ovStatus === "source_verified";
  const isVerifiedRow = ovStatus === "verified";
  const lifecycleStatus = String(bet?.lifecycleStatus || "").trim();
  const oddsAgeSec =
    typeof bet?.oldest_odds_age_seconds === "number" && Number.isFinite(bet.oldest_odds_age_seconds)
      ? bet.oldest_odds_age_seconds
      : null;
  const sourceAgeSec =
    ovInfo && typeof ovInfo.sourceAgeSeconds === "number" && Number.isFinite(ovInfo.sourceAgeSeconds)
      ? ovInfo.sourceAgeSeconds
      : null;
  const rawAgeSec =
    ovInfo && typeof ovInfo.rawAgeSeconds === "number" && Number.isFinite(ovInfo.rawAgeSeconds)
      ? ovInfo.rawAgeSeconds
      : null;
  const lastUpdateMs = getValueRowUpdateMs(bet);
  const lastUpdateLabel = lastUpdateMs > 0 ? formatAnalysisTime(lastUpdateMs, lang, timeZone) : "";
  const deepLink = String(bet?.deep_link ?? bet?.deepLink ?? "").trim();
  const lineLabel = String(canonicalDisplay?.lineLabel || "").trim() || buildValueDisplayLineText(bet) || pickLineFromValueBet(bet) || "—";
  const oddsHkDisplay =
    (oddsFormat === "hk" ? buildValueOfferedOddsHkDisplay(bet) : "") ||
    formatAnalysisOddsDisplay(bet.odds, oddsFormat) ||
    String(bet.odds ?? "—");
  const trueOddsHkDisplay =
    (oddsFormat === "hk" ? buildValueTrueOddsHkDisplay(bet) : "") ||
    formatTrueOddsHkDisplay(bet.trueOdds, oddsFormat) || "—";
  const trueOddsSource = getTrueOddsSourceName(bet);
  const verificationLabel = isVerifiedRow ? "Raw Verified" : isSourceRow ? "Source Verified" : isGraceRow ? "Grace" : "—";
  const readinessLabelForCopy = verificationState.readinessLabel || "Not ready";
  const copyText = [
    `Match: ${String(bet.match || "").trim() || "—"}`,
    `League: ${String((isEsports ? esportsLeague : displayLeague) || "").trim() || "—"}`,
    `Market: ${String(displayMarket || "").trim() || "—"}`,
    `Selection: ${String(copySelectionLabel || bet.selection || "").trim() || "—"}`,
    `Line: ${lineLabel}`,
    `Book cược: ${offerBook || "—"}`,
    `Tỷ lệ cược: ${String(oddsHkDisplay)}`,
    `Nguồn tỷ lệ thực: ${trueOddsSource || "SharpAPI"}`,
    `Tỷ lệ thực: ${String(trueOddsHkDisplay || "—")}`,
    `EV: ${String(bet.ev ?? "—")}%`,
    `Verification: ${verificationLabel}`,
    `Readiness: ${readinessLabelForCopy}`,
    `Updated: ${lastUpdateLabel || "—"}`,
  ].join("\n");
  const canQuickBet = !isDebugWatchlist && !!deepLink;
  const cardClassParts = ["vb-card"];
  if (isFlashing) cardClassParts.push("is-flash");
  if (isLeaving) cardClassParts.push("is-leaving");
  if (isVerifiedRow) cardClassParts.push("is-raw");
  if (isSourceRow) cardClassParts.push("is-source");
  if (isGraceRow) cardClassParts.push("is-grace-row");
  if (verificationState.staleLiveRawRecheck) cardClassParts.push("is-stale-live-recheck");
  if (isDebugWatchlist) cardClassParts.push("is-debug-watchlist");

  return (
    <div className={cardClassParts.join(" ")}>
      <div className="vb-top-row">
        <div className="vb-league">{isEsports ? esportsLeague : displayLeague}</div>

        {hasLiveGameState && gsPeriodClock ? (
          <div className="vb-gs-clock-pill">{gsPeriodClock}</div>
        ) : (
          <div
            className={`vb-status-time-pill ${statusInfo.isLive ? "is-live" : "is-prematch"}`}
          >
            <span className="vb-status-time-value">
              {statusInfo.isLive && gameState && gameStatePeriodLabel
                ? gameStatePeriodLabel
                : displayTimeValue}
            </span>
          </div>
        )}
      </div>

      <div className="vb-match">
        {hasLiveGameState && gsScore ? (() => {
          const { homeScore, awayScore } = gsScore;
          const { home: homeTeam, away: awayTeam } = gsTeams || { home: "", away: "" };
          return (
            <div className="vb-match-live-score">
              <span className="vb-match-team vb-match-team-home">{homeTeam}</span>
              {gsPossession === "home" && (
                <span className="vb-possession-ball" title="Đang có bóng">⚽</span>
              )}
              <span className="vb-match-score">{homeScore ?? 0}</span>
              <span className="vb-match-dash">-</span>
              <span className="vb-match-score">{awayScore ?? 0}</span>
              {gsPossession === "away" && (
                <span className="vb-possession-ball" title="Đang có bóng">⚽</span>
              )}
              <span className="vb-match-team vb-match-team-away">{awayTeam}</span>
            </div>
          );
        })() : (() => {
          const homeTeam = String(bet?.home_team ?? bet?.homeTeam ?? "").trim();
          const awayTeam = String(bet?.away_team ?? bet?.awayTeam ?? "").trim();
          if (homeTeam && awayTeam) {
            return (
              <div className="vb-match-prematch">
                <span className="vb-match-team">{homeTeam}</span>
                <span className="vb-match-dash"> – </span>
                <span className="vb-match-team">{awayTeam}</span>
              </div>
            );
          }
          return renderMatchWithColoredAt(bet.match);
        })()}
        <button
          type="button"
          className={`value-inline-copy-button ${isCopied ? "is-ok" : ""}`.trim()}
          title="Copy tên đội"
          aria-label="Copy tên đội"
          onClick={() => {
            const homeName = String(bet?.home_team ?? bet?.homeTeam ?? bet?.match ?? "").trim();
            onCopy(stableKey, homeName);
          }}
        >
          {isCopied ? "✓" : "📋"}
        </button>
      </div>

      <div className="vb-row">
        {showSelectionRow && selectionLabel ? (
          <div className="vb-pick">{displayMarket}</div>
        ) : (
        <div className="vb-pick">
            {displayMarket}
          </div>
        )}
        {lastUpdateLabel ? (
          <span className="vb-updated-inline">Updated {lastUpdateLabel}</span>
        ) : null}
      </div>

      {showSelectionRow && selectionLabel ? (
        <div className="vb-selection-row">
        <div className="vb-selection-head">
            <div className="vb-selection-label">{selectionLabel}</div>
          </div>
          {teamTotalNeedsSideWarning ? (
            <span className="vb-team-side-warning" title="Team total side needs bookmaker check">
              Cần kiểm đội
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="vb-row vb-odds-row">
        <div className="vb-odds vb-odds-block vb-odds-block-offer">
          <span className="vb-odds-value">
            {oddsLabel} {oddsHkDisplay}
          </span>
          {offerBook ? (
            <span
              className="vb-odds-source-line vb-odds-source-book"
              title={`Nhà cái: ${offerBook}`}
            >
              <span className="vb-odds-source-muted">Nhà cái:</span>
              <span className="vb-odds-source-name">{offerBook}</span>
            </span>
          ) : null}
        </div>
        <div className="vb-odds vb-odds-block vb-odds-block-fair">
          <span className="vb-odds-value">
            {trueOddsLabel} {trueOddsHkDisplay}
          </span>
          <span
            className="vb-odds-source-line vb-odds-source-fair"
            title={`Nguồn chuẩn: ${trueOddsSource || "SharpAPI"}`}
          >
            <span className="vb-odds-source-muted">Nguồn chuẩn:</span>
            <span className="vb-odds-source-name">
              {trueOddsSource || "SharpAPI"}
            </span>
          </span>
        </div>
        <div className="vb-ev">{formatValueEvPercentForDisplay(bet.ev)}</div>
      </div>

      {isDebugWatchlist ? (
        <div className="vb-action-row vb-action-row-debug">
          <div className="vb-debug-watchlist-banner" role="status">
            Cần kiểm — Không phải kèo vào cược
          </div>
        </div>
      ) : (
        <div className="vb-action-row">
          {canQuickBet ? (
            <button
              type="button"
              className="vb-quick-action is-strong"
              onClick={() => window.open(deepLink, "_blank", "noopener,noreferrer")}
              title="Mở nhà cái"
            >
              Vào cược
            </button>
          ) : (
            <span className="vb-quick-action-placeholder" />
          )}
          <div className="vb-action-right">
            {(() => {
              const splitData = splits ?? null;
              if (!splitData) return null;
              const toNum = (v) => typeof v === "number" && Number.isFinite(v) ? v : null;
              const handlePct = toNum(splitData.splitsHandlePct);
              const betsPct   = toNum(splitData.splitsBetPct);
              if (handlePct == null && betsPct == null) return null;
              const handleSharp = handlePct != null ? Math.round(handlePct) : null;
              const betsPublic  = betsPct  != null ? Math.round(betsPct)  : null;
              if (handleSharp == null && betsPublic == null) return null;
              return (
                <div className="vb-splits-pill" title="Betting Splits từ Sharp API">
                  {handleSharp != null && (
                    <span className="vb-splits-sharp">💰{handleSharp}% tiền</span>
                  )}
                  {betsPublic != null && (
                    <span className="vb-splits-public">🎟{betsPublic}% vé</span>
                  )}
                </div>
              );
            })()}
            {hasLiveGameState && gsStatRows.length > 0 && (() => {
              const compactRows = gsStatRows.slice(0, 3);
              return (
                <div className="vb-gs-compact">
                  {compactRows.map((row) => (
                    <span className="vb-gs-compact-stat" key={row.key}>
                      {row.icon} {row.home ?? 0}-{row.away ?? 0}
                    </span>
                  ))}
                  <button
                    type="button"
                    className={`vb-gs-detail-btn${showDetail ? " is-open" : ""}`}
                    onClick={() => setShowDetail((v) => !v)}
                    title="Chi tiết trận đấu"
                  >
                    {showDetail ? "▴" : "▾"}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showDetail && hasLiveGameState && gsStatRows.length > 0 && (() => {
        const { home: homeTeam, away: awayTeam } = gsTeams || { home: "", away: "" };
        const { homeScore, awayScore } = gsScore || { homeScore: 0, awayScore: 0 };
        return (
          <div className="vb-gs-detail-panel">
            <div className="vb-gs-detail-score-row">
              <span className="vb-gs-detail-team vb-gs-detail-home">
                {homeTeam}
                {gsPossession === "home" && (
                  <span className="vb-gs-ball vb-gs-ball-anim" title="Đang có bóng">⚽</span>
                )}
              </span>
              <span className="vb-gs-detail-score">
                {homeScore ?? 0} – {awayScore ?? 0}
              </span>
              <span className="vb-gs-detail-team vb-gs-detail-away">
                {gsPossession === "away" && (
                  <span className="vb-gs-ball vb-gs-ball-anim" title="Đang có bóng">⚽</span>
                )}
                {awayTeam}
              </span>
            </div>
            {gsStatRows.map((row) => (
              <div key={row.key} className="vb-gs-detail-stat-row">
                <span className="vb-gs-stat-num vb-gs-stat-home">{row.home ?? 0}</span>
                <span className="vb-gs-stat-label">{row.label} {row.icon}</span>
                <span className="vb-gs-stat-num vb-gs-stat-away">{row.away ?? 0}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {isDebugWatchlist ? (
        <div className="vb-debug-watchlist-note" role="note">
          Raw chưa xác minh
        </div>
      ) : null}


      {(bet.possibly_stale || (Array.isArray(bet.warnings) && bet.warnings.length > 0)) ? (
        <div className="arb-warning-row">
          {bet.possibly_stale && (
            <span className="arb-warning-badge arb-warning-stale" title="Odds có thể đã thay đổi">
              ⚠ Odds cũ
            </span>
          )}
          {Array.isArray(bet.warnings) && bet.warnings.includes("POTENTIALLY_STALE_ODDS") && (
            <span className="arb-warning-badge arb-warning-stale" title="Odds nghi ngờ bị trễ">
              ⚠ Odds trễ
            </span>
          )}
          {Array.isArray(bet.warnings) && bet.warnings.includes("VERY_STALE_ODDS") && (
            <span className="arb-warning-badge arb-warning-very-stale" title="Odds chắc chắn đã cũ">
              ⚠ Odds cũ nhiều
            </span>
          )}
          {Array.isArray(bet.warnings) && bet.warnings.includes("LIVE_HIGH_PROFIT_SUSPICIOUS") && (
            <span className="arb-warning-badge arb-warning-suspicious" title="EV cao bất thường, có thể là lỗi odds">
              ⚠ Nghi ngờ lỗi
            </span>
          )}
        </div>
      ) : null}

    </div>
  );
});

function normalizeSplitsSelectionKey(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  if (s === "tài" || s === "tai" || s.startsWith("over") || s === "o") return "over";
  if (s === "xỉu" || s === "xiu" || s.startsWith("under") || s === "u") return "under";
  if (s === "hòa" || s === "hoa" || s === "draw") return "draw";
  return s;
}

function normalizeSplitsMarketKey(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  // Total markets — "total_points" is what Sharp API value bets use
  if (s === "total" || s === "totals" || s === "total_points"
      || s === "game_total" || s === "game_totals"
      || s === "ou" || s.includes("over_under")
      || s.includes("tài/xỉu") || s.includes("tai/xiu")) return "total";
  // Spread markets
  if (s === "spread" || s === "spreads" || s === "pointspread"
      || s === "handicap" || s === "asian_handicap"
      || s.includes("run_line") || s.includes("puck_line")) return "spread";
  // Moneyline markets
  if (s === "moneyline" || s === "moneylines" || s === "h2h"
      || s === "1x2" || s === "game_winner"
      || s.includes("winner")) return "moneyline";
  // Everything else (player props, team totals, quarters) → no splits
  return "";
}

function buildGameStateKey(home, away) {
  const h = String(home ?? "").trim().toLowerCase();
  const a = String(away ?? "").trim().toLowerCase();
  if (!h || !a) return "";
  return `${h}|${a}`;
}

function isGameStateStale(state) {
  return state?.stale === true || state?.aggregator_stale === true;
}

function extractGameStateExtras(state) {
  if (!state || typeof state !== "object") return null;
  const sport = String(state.sport ?? "").toLowerCase();
  const extras = {};

  if (sport === "soccer") {
    if (state.corners_home != null)
      extras.corners = { home: state.corners_home, away: state.corners_away ?? 0 };
    if (state.yellow_cards_home != null)
      extras.yellowCards = { home: state.yellow_cards_home, away: state.yellow_cards_away ?? 0 };
    if (state.red_cards_home != null && (state.red_cards_home > 0 || (state.red_cards_away ?? 0) > 0))
      extras.redCards = { home: state.red_cards_home, away: state.red_cards_away ?? 0 };
    if (state.fouls_home != null)
      extras.fouls = { home: state.fouls_home, away: state.fouls_away ?? 0 };
    if (state.possession)
      extras.possession = state.possession;
  }

  if (sport === "basketball") {
    if (state.fouls_home != null)
      extras.fouls = { home: state.fouls_home, away: state.fouls_away ?? 0 };
    if (state.is_timeout === true) extras.isTimeout = true;
    if (state.possession) extras.possession = state.possession;
  }

  if (sport === "hockey") {
    if (state.power_play) extras.powerPlay = state.power_play;
  }

  if (sport === "tennis") {
    if (state.sets_home != null)
      extras.sets = { home: state.sets_home, away: state.sets_away ?? 0 };
    if (state.server) extras.server = state.server;
  }

  if (sport === "baseball") {
    if (state.hits_home != null)
      extras.hits = { home: state.hits_home, away: state.hits_away ?? 0 };
    if (state.home_pitcher) extras.homePitcher = state.home_pitcher;
    if (state.away_pitcher) extras.awayPitcher = state.away_pitcher;
  }

  if (sport === "cricket") {
    if (state.wickets_home != null)
      extras.wickets = { home: state.wickets_home, away: state.wickets_away ?? 0 };
    if (state.overs != null) extras.overs = state.overs;
    if (state.batting_team) extras.battingTeam = state.batting_team;
  }

  if (state.last_play) extras.lastPlay = state.last_play;

  return Object.keys(extras).length > 0 ? extras : null;
}

function formatGameStatePeriodLabel(state) {
  const period = String(state?.game_period ?? "").trim();
  const clock  = String(state?.game_clock  ?? "").trim();
  if (period && clock) return `${period} • ${clock}`;
  if (period) return period;
  if (clock) return clock;
  return "";
}

function hasGameStateData(gs) {
  return gs != null && typeof gs === "object";
}

function _firstNonEmpty(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
}

function _toGSNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getGameStatePeriodClock(gs) {
  const period = _firstNonEmpty(gs?.period, gs?.period_name, gs?.periodName, gs?.half, gs?.phase, gs?.game_period);
  const clock  = _firstNonEmpty(gs?.clock, gs?.game_clock, gs?.gameClock, gs?.time, gs?.match_time, gs?.matchTime);
  const p = String(period || "").trim();
  const c = String(clock  || "").trim();
  if (p && c) return `${p} • ${c}`;
  return p || c || "";
}

function getGameStateTeams(bet, gs) {
  const home = _firstNonEmpty(
    gs?.home_team, gs?.homeTeam, gs?.teams?.home?.name,
    bet?.home_team, bet?.homeTeam, bet?.homeTeamName
  ) || "";
  const away = _firstNonEmpty(
    gs?.away_team, gs?.awayTeam, gs?.teams?.away?.name,
    bet?.away_team, bet?.awayTeam, bet?.awayTeamName
  ) || "";
  return { home: String(home).trim(), away: String(away).trim() };
}

function getGameStateScore(gs) {
  const homeScore = _toGSNumber(_firstNonEmpty(
    gs?.home_score, gs?.homeScore, gs?.score_home, gs?.scoreHome, gs?.scores?.home
  ));
  const awayScore = _toGSNumber(_firstNonEmpty(
    gs?.away_score, gs?.awayScore, gs?.score_away, gs?.scoreAway, gs?.scores?.away
  ));
  return { homeScore, awayScore };
}

function getGameStatePossessionSide(gs) {
  const raw = String(
    _firstNonEmpty(gs?.possession, gs?.possession_side, gs?.possessionSide, gs?.team_in_possession, gs?.teamInPossession) || ""
  ).toLowerCase();
  if (raw.includes("home") || raw === "1") return "home";
  if (raw.includes("away") || raw === "2") return "away";
  return "";
}

function getGameStateStatRows(gs) {
  const sport = String(gs?.sport || "").toLowerCase();
  const rows = [];
  if (sport === "soccer" || sport === "football" || !sport) {
    rows.push(
      { key: "corners", icon: "⛳", label: "Phạt Góc",
        home: _toGSNumber(_firstNonEmpty(gs?.corners_home, gs?.cornersHome)),
        away: _toGSNumber(_firstNonEmpty(gs?.corners_away, gs?.cornersAway)) },
      { key: "yellow", icon: "🟨", label: "Thẻ Vàng",
        home: _toGSNumber(_firstNonEmpty(gs?.yellow_cards_home, gs?.yellowCardsHome)),
        away: _toGSNumber(_firstNonEmpty(gs?.yellow_cards_away, gs?.yellowCardsAway)) },
      { key: "red", icon: "🟥", label: "Thẻ Đỏ",
        home: _toGSNumber(_firstNonEmpty(gs?.red_cards_home, gs?.redCardsHome)),
        away: _toGSNumber(_firstNonEmpty(gs?.red_cards_away, gs?.redCardsAway)) },
      { key: "fouls_s", icon: "⚠️", label: "Phạm Lỗi",
        home: _toGSNumber(_firstNonEmpty(gs?.fouls_home, gs?.foulsHome)),
        away: _toGSNumber(_firstNonEmpty(gs?.fouls_away, gs?.foulsAway)) }
    );
  }
  if (sport === "basketball") {
    rows.push(
      { key: "fouls_b", icon: "⚠️", label: "Phạm Lỗi",
        home: _toGSNumber(_firstNonEmpty(gs?.fouls_home, gs?.foulsHome)),
        away: _toGSNumber(_firstNonEmpty(gs?.fouls_away, gs?.foulsAway)) }
    );
  }
  if (sport === "hockey") {
    const pp = String(gs?.power_play || "").toLowerCase();
    if (pp === "home" || pp === "away") {
      rows.push(
        { key: "power_play", icon: "⚡", label: "Power Play",
          home: pp === "home" ? 1 : 0,
          away: pp === "away" ? 1 : 0 }
      );
    }
  }
  if (sport === "tennis" || sport === "table_tennis") {
    rows.push(
      { key: "sets", icon: "🎾", label: "Sets",
        home: _toGSNumber(_firstNonEmpty(gs?.sets_home, gs?.setsHome)),
        away: _toGSNumber(_firstNonEmpty(gs?.sets_away, gs?.setsAway)) }
    );
  }
  return rows.filter((r) => r.home != null || r.away != null);
}

export default function ValueBetsPage({ active = true, lang = "en-GB", timeZone, plan = "basic", oddsFormat = "hk" }) {
  const t = useMemo(() => getAnalysisTranslations(lang), [lang]);
  const canonicalPlan = useMemo(() => normalizeAnalysisPlan(plan), [plan]);
  const allowedEvOptions = useMemo(
    () => getAllowedEvOptionsForPlan(canonicalPlan),
    [canonicalPlan]
  );

  const [bets, setBets] = useState([]);
  const [hasReceivedValueBets, setHasReceivedValueBets] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [evFilter, setEvFilter] = useState(1);
  const [sport, setSport] = useState("football");
  const [mode, setMode] = useState("all"); // all | live | prematch
  const [isEvOpen, setIsEvOpen] = useState(false);
  const [flashKeys, setFlashKeys] = useState(new Set());
  const [copiedKey, setCopiedKey] = useState("");
  const [seenBySport, setSeenBySport] = useState({
    football: new Set(),
    tennis: new Set(),
    basketball: new Set(),
    esports: new Set(),
  });

  const prevStableKeys = useRef(new Set());
  // Maps stable key → timeout ID for grace period removal
  const leavingTimersRef = useRef(new Map()); // removal timers
  const fadeTimersRef    = useRef(new Map()); // fade-start timers
  // Set of stable keys currently in grace period (will be removed soon)
  const [leavingKeys, setLeavingKeys] = useState(new Set());
  const [splitsMap, setSplitsMap] = useState({});
  const [gameStateMap, setGameStateMap] = useState({});
  const pollTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const flashClearTimeoutRef = useRef(null);
  const evDropdownRef = useRef(null);
  const activeRef = useRef(active);
  const modeRef = useRef(mode);
  const analysisWsRef = useRef(null);
  const analysisWsReconnectTimerRef = useRef(null);
  const analysisWsAttemptRef = useRef(0);
  const pushTransportActiveRef = useRef(false);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    if (!bets || bets.length === 0) return;
    let cancelled = false;

    const fetchSplits = async () => {
      try {
        const base = await detectAnalysisApiBase();
        const res = await fetch(`${base}/api/analysis/splits`);
        if (!res.ok || cancelled) return;
        const json = await res.json();
        if (!json?.ok || !Array.isArray(json.data) || cancelled) return;
        const map = {};
        json.data.forEach((row) => {
          if (!row?.hasSplits) return;
          const eid  = String(row?.eventId ?? row?.event_id ?? "").trim();
          const mkt  = String(row?.marketType ?? "").trim().toLowerCase();
          const sel  = String(row?.selection  ?? "").trim().toLowerCase();
          const home = String(row?.homeTeam   ?? row?.home_team ?? "").trim().toLowerCase();
          const away = String(row?.awayTeam   ?? row?.away_team ?? "").trim().toLowerCase();
          if (!mkt || !sel) return;
          // Primary: team-name composite (most reliable across endpoints)
          if (home && away) {
            map[`${home}|${away}||${mkt}||${sel}`] = row;
          }
          // Secondary: eventId composite
          if (eid) {
            map[`${eid}||${mkt}||${sel}`] = row;
          }
        });
        setSplitsMap(map);
      } catch { /* ignore */ }
    };

    fetchSplits();
    const interval = setInterval(fetchSplits, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [bets]);

  useEffect(() => {
    let cancelled = false;
    const fetchGameState = async () => {
      try {
        const base = await detectAnalysisApiBase();
        const res = await fetch(`${base}/api/analysis/gamestate`);
        if (!res.ok || cancelled) return;
        const json = await res.json();
        if (!json?.data || typeof json.data !== "object" || cancelled) return;
        const map = {};
        Object.values(json.data).forEach((sportBucket) => {
          if (!sportBucket || typeof sportBucket !== "object") return;
          Object.values(sportBucket).forEach((state) => {
            if (!state || typeof state !== "object") return;
            if (isGameStateStale(state)) return;
            const key = buildGameStateKey(state.home_team, state.away_team);
            if (key) map[key] = state;
          });
        });
        if (!cancelled) setGameStateMap(map);
      } catch { /* ignore */ }
    };
    fetchGameState();
    const interval = setInterval(fetchGameState, 20000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setEvFilter((prev) => {
      if (allowedEvOptions.includes(prev)) return prev;
      return clampToNearestNumberOption(prev, allowedEvOptions);
    });
  }, [allowedEvOptions]);

  const oddsLabel = t?.common?.oddsLabel || t?.common?.odds || "Odds";
  const trueOddsLabel = t?.common?.trueOddsLabel || t?.value?.trueOdds || "True Odds";

  useEffect(() => {
    if (!active) {
      pushTransportActiveRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
      if (flashClearTimeoutRef.current) {
        clearTimeout(flashClearTimeoutRef.current);
        flashClearTimeoutRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const applyNormalizedList = (normalized) => {
      const stableKeys = normalized.map((b) => getValueRenderIdentityKey(b)).filter(Boolean);
      const stableKeySet = new Set(stableKeys);

      // ── Flash new keys ────────────────────────────────────────
      const newStableKeys = [];
      for (let i = 0; i < stableKeys.length; i += 1) {
        const k = stableKeys[i];
        if (!prevStableKeys.current.has(k)) newStableKeys.push(k);
      }

      // ── Grace period: solid 15s then quick 0.4s fade-out ─────
      const GRACE_MS = 15000;  // total hold time
      const FADE_MS  = 400;    // quick fade duration (matches enter)
      const timers      = leavingTimersRef.current;
      const fadeTimers  = fadeTimersRef.current;

      // Keys that just reappeared → cancel both timers
      for (const k of stableKeys) {
        if (fadeTimers.has(k)) {
          clearTimeout(fadeTimers.get(k));
          fadeTimers.delete(k);
        }
        if (timers.has(k)) {
          clearTimeout(timers.get(k));
          timers.delete(k);
          setLeavingKeys((prev) => {
            const next = new Set(prev);
            next.delete(k);
            return next;
          });
        }
      }

      // Keys that just disappeared → solid hold, then quick fade
      for (const k of prevStableKeys.current) {
        if (!stableKeySet.has(k) && !timers.has(k)) {
          // Timer 1: start fade-out FADE_MS before removal
          const fadeTid = setTimeout(() => {
            fadeTimers.delete(k);
            setLeavingKeys((prev) => new Set([...prev, k]));
          }, GRACE_MS - FADE_MS);
          fadeTimers.set(k, fadeTid);

          // Timer 2: actually remove from DOM after full GRACE_MS
          const removeTid = setTimeout(() => {
            timers.delete(k);
            setLeavingKeys((prev) => {
              const next = new Set(prev);
              next.delete(k);
              return next;
            });
            setBets((prev) => prev.filter(
              (b) => getValueRenderIdentityKey(b) !== k
            ));
          }, GRACE_MS);
          timers.set(k, removeTid);
        }
      }

      prevStableKeys.current = stableKeySet;

      // ── Flash new keys ────────────────────────────────────────
      if (newStableKeys.length) {
        if (flashClearTimeoutRef.current) {
          clearTimeout(flashClearTimeoutRef.current);
          flashClearTimeoutRef.current = null;
        }
        setFlashKeys(new Set(newStableKeys));
        flashClearTimeoutRef.current = setTimeout(() => {
          setFlashKeys(new Set());
          flashClearTimeoutRef.current = null;
        }, 2000);
      }

      setHasReceivedValueBets(true);
      setFetchError(null);
      // Merge: keep leaving rows in bets so they stay rendered
      // during grace period, but update data for active rows.
      setBets((prev) => {
        const activeMap = new Map(
          normalized.map((b) => [getValueRenderIdentityKey(b), b])
        );
        // Keep leaving rows (not in new list) from prev
        const leavingRows = prev.filter(
          (b) => !activeMap.has(getValueRenderIdentityKey(b))
            && timers.has(getValueRenderIdentityKey(b))
        );
        return [...normalized, ...leavingRows];
      });
    };

    const normalizeMergedRaw = (mergedRaw) => {
      const normalized = mergedRaw.map(normalizeIncomingValueBet).filter(Boolean);
      return normalized;
    };

    const clearPoll = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const clearWsReconnect = () => {
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
    };

    const scheduleWsReconnect = () => {
      clearWsReconnect();
      const attempt = analysisWsAttemptRef.current;
      const delayMs = Math.min(30000, Math.round(1000 * Math.pow(1.5, attempt)));
      analysisWsReconnectTimerRef.current = setTimeout(() => {
        analysisWsReconnectTimerRef.current = null;
        connectAnalysisWs();
      }, delayMs);
    };

    const scheduleRestPoll = () => {
      clearPoll();
      const ms = modeRef.current === "prematch" ? 45000 : 8000;
      pollTimerRef.current = setInterval(() => {
        restTick();
      }, ms);
    };

    const restTick = async () => {
      if (!activeRef.current || inFlightRef.current) return;
      inFlightRef.current = true;
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const base = await detectAnalysisApiBase();
        const [liveRes, earlyRes] = await Promise.all([
          fetch(`${base}/api/analysis/value-bets/list?scope=live`, { signal: ac.signal }),
          fetch(`${base}/api/analysis/value-bets/list?scope=early`, { signal: ac.signal }),
        ]);
        if (!liveRes.ok || !earlyRes.ok) throw new Error("http");
        const jl = await liveRes.json();
        const je = await earlyRes.json();
        if (!jl || jl.ok !== true || !je || je.ok !== true) throw new Error("json");
        const mergedRaw = mergeRawValueBets(jl.results || [], je.results || []);
        applyNormalizedList(normalizeMergedRaw(mergedRaw));
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (!pushTransportActiveRef.current) setFetchError("fetch_failed");
      } finally {
        inFlightRef.current = false;
      }
    };

    const connectAnalysisWs = async () => {
      if (cancelled || !activeRef.current) return;
      const base = await detectAnalysisApiBase();
      const wsUrl = httpBaseToAnalysisWsUrl(base);
      if (!wsUrl || typeof WebSocket === "undefined") {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        await restTick();
        scheduleRestPoll();
        scheduleWsReconnect();
        return;
      }
      try {
        const ws = new WebSocket(wsUrl);
        analysisWsRef.current = ws;
        ws.onopen = () => {
          if (cancelled || analysisWsRef.current !== ws) return;
          analysisWsAttemptRef.current = 0;
          pushTransportActiveRef.current = true;
          clearPoll();
          clearWsReconnect();
        };
        ws.onmessage = (ev) => {
          if (cancelled || analysisWsRef.current !== ws) return;
          let msg;
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (!msg || typeof msg !== "object") return;
          if (msg.type === "value_bets_list" && msg.ok === true) {
            const mergedRaw = Array.isArray(msg.results) ? msg.results : [];
            applyNormalizedList(normalizeMergedRaw(mergedRaw));
          }
        };
        ws.onerror = (e) => {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        };
        ws.onclose = (e) => {
          if (analysisWsRef.current === ws) analysisWsRef.current = null;
          if (cancelled) return;
          pushTransportActiveRef.current = false;
          analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
          scheduleRestPoll();
          scheduleWsReconnect();
        };
      } catch {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        restTick();
        scheduleRestPoll();
        scheduleWsReconnect();
      }
    };

    (async () => {
      await restTick();
      if (cancelled) return;
      await connectAnalysisWs();
    })();

    return () => {
      cancelled = true;
      clearPoll();
      clearWsReconnect();
      pushTransportActiveRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
      if (flashClearTimeoutRef.current) {
        clearTimeout(flashClearTimeoutRef.current);
        flashClearTimeoutRef.current = null;
      }
    };
  }, [active]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!evDropdownRef.current) return;
      if (!evDropdownRef.current.contains(e.target)) {
        setIsEvOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsEvOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const isLiveMatch = (b) => {
    const status = String(b?.status ?? b?.matchStatus ?? b?.state ?? b?.phase ?? "").toLowerCase();

    if (b?.isLive === true) return true;
    if (
      status.includes("live") ||
      status.includes("inplay") ||
      status.includes("in-play") ||
      status.includes("playing")
    ) {
      return true;
    }

    return false;
  };

  const VALUE_MIN_VISIBLE_HK_ODDS = 0.70;
  const VALUE_MAX_VISIBLE_HK_ODDS = 2.00;

  const VALUE_MIN_EV_BY_SPORT_SCOPE = {
    football: { prematch: 3.5, live: 5.5 },
    tennis: { prematch: 4.5, live: 6.5 },
    basketball: { prematch: 3.0, live: 5.0 },
    esports: { prematch: 6.0, live: 8.0 },
  };

  const getValueMinEvThresholdForBet = (bet) => {
    const live = isLiveMatch(bet);
    const scopeKey = live ? "live" : "prematch";
    // Diagnostic-only: use the bet's real/canonical sport (not the Value tab
    // it is routed to), so the "below recommended EV" diagnostic reflects the
    // bet's true sport rather than the Esports fallback tab it may land in.
    const betCanonicalSport = getBetCanonicalSport(bet);
    const threshold = VALUE_MIN_EV_BY_SPORT_SCOPE?.[betCanonicalSport]?.[scopeKey];
    if (typeof threshold === "number" && Number.isFinite(threshold)) return threshold;
    return live ? 6.0 : 4.0;
  };

  const filtered = useMemo(() => {
    const selectedEvMinRaw = Number(evFilter);
    const selectedEvMin = Number.isFinite(selectedEvMinRaw) ? selectedEvMinRaw : 1;
    return bets.filter((b) => {
      const live = isLiveMatch(b);
      const betSport = getBetSport(b);

      if (mode === "live" && !live) return false;
      if (mode === "prematch" && live) return false;
      if (betSport !== sport) return false;
      if (b.ev < selectedEvMin) return false;

      return true;
    });
  }, [bets, mode, sport, evFilter]);

  const visibleFiltered = useMemo(() => {
    // Do NOT hide backend-published source_verified rows solely because their
    // HK odds are outside a hardcoded display range. We keep rows whose HK odds
    // are either absent or finite (real numeric odds always pass).
    const kept = filtered.filter((b) => {
      const hk = valueBetOfferedOddsToHkNumber(b);
      return hk == null || Number.isFinite(hk);
    });

    // Sort: 1) Raw verified + raw_ready + fresh, 2) Source verified + source_check_required,
    // 3) Grace + grace_check_required, 4) Higher EV, 5) Newer updated time, 6) Lower raw/source age.
    const deduped = mergeValueRowsBySemanticOpportunity(kept);
    const withRank = deduped.map((b, i) => ({
      bet: b,
      rank: getValueRowReadinessRank(b),
      ev: typeof b.ev === "number" && Number.isFinite(b.ev) ? b.ev : -Infinity,
      ts: getValueRowUpdateMs(b),
      age: (() => {
        const state = getValueBetCommercialState(b);
        const sourceAge =
          b?.offerVerification &&
          typeof b.offerVerification.sourceAgeSeconds === "number" &&
          Number.isFinite(b.offerVerification.sourceAgeSeconds) &&
          b.offerVerification.sourceAgeSeconds >= 0
            ? b.offerVerification.sourceAgeSeconds
            : null;
        const rawAge = state.rawAgeSec;
        const best = Math.min(
          rawAge != null ? rawAge : Number.POSITIVE_INFINITY,
          sourceAge != null ? sourceAge : Number.POSITIVE_INFINITY
        );
        return Number.isFinite(best) ? best : Number.POSITIVE_INFINITY;
      })(),
      i,
    }));
    withRank.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (a.ev !== b.ev) return b.ev - a.ev;
      if (a.ts !== b.ts) return b.ts - a.ts;
      if (a.age !== b.age) return a.age - b.age;
      return a.i - b.i;
    });
    return withRank.map((x) => x.bet);
  }, [filtered]);

  /**
   * Public-safe Raw Verified bucket. These are the ONLY rows the public Value
   * tab is allowed to render as normal actionable betting rows. Anything that
   * is not Raw Verified / raw_ready / active / fresh / unambiguous is excluded
   * here and (when the debug flag is enabled) optionally surfaced under a
   * separated "Watchlist" section instead.
   */
  const publicRawReadyVisible = useMemo(
    () => visibleFiltered,
    [visibleFiltered]
  );

  const publicSourceCheckVisible = useMemo(
    () => [],
    []
  );

  // Dev-only diagnostic snapshot used inside the Value tab empty state. This
  // does NOT participate in any filter / render decisions for actual bet rows
  // — it merely mirrors the existing filter pipeline counters so we can see
  // exactly where rows are being dropped (mode, sport, EV, raw vs source
  // bucket) when the tab appears empty. The same object is also logged via
  // console.debug from a dev-only effect below.
  const valueEmptyDiagnostics = useMemo(() => {
    const selectedEvMinRaw = Number(evFilter);
    const selectedEvMin = Number.isFinite(selectedEvMinRaw) ? selectedEvMinRaw : 1;
    let afterModeFilter = 0;
    let afterSportFilter = 0;
    let afterEvFilter = 0;
    let rawReadyCandidates = 0;
    let sourceVerifiedCandidates = 0;
    for (let i = 0; i < bets.length; i += 1) {
      const b = bets[i];
      const live = isLiveMatch(b);
      if (mode === "live" && !live) continue;
      if (mode === "prematch" && live) continue;
      afterModeFilter += 1;
      const betSport = getBetSport(b);
      if (betSport !== sport) continue;
      afterSportFilter += 1;
      if (typeof b?.ev === "number" && b.ev < selectedEvMin) continue;
      afterEvFilter += 1;
      if (isPublicRawReadyRow(b)) rawReadyCandidates += 1;
      if (isPublicSourceCheckRow(b)) sourceVerifiedCandidates += 1;
    }
    const sampleFirstFive = bets.slice(0, 5).map((b) => ({
      sport: b?.sport ?? null,
      league: b?.league ?? null,
      ev: typeof b?.ev === "number" && Number.isFinite(b.ev) ? b.ev : null,
      offerVerificationStatus:
        b && b.offerVerification && typeof b.offerVerification === "object"
          ? b.offerVerification.status ?? null
          : null,
      betReadiness: b?.betReadiness ?? null,
      lifecycleStatus: b?.lifecycleStatus ?? null,
      getBetSport: getBetSport(b),
      isLiveMatch: isLiveMatch(b),
    }));
    return {
      betsLength: bets.length,
      selectedMode: mode,
      selectedSport: sport,
      selectedEvFilter: evFilter,
      afterModeFilter,
      afterSportFilter,
      afterEvFilter,
      rawReadyCandidates,
      sourceVerifiedCandidates,
      publicRawReadyVisibleLength: publicRawReadyVisible.length,
      publicSourceCheckVisibleLength: publicSourceCheckVisible.length,
      sampleFirstFive,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bets, mode, sport, evFilter, publicRawReadyVisible, publicSourceCheckVisible]);

  const isValueDevDiagnosticsEnabled = (() => {
    try {
      if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
        return true;
      }
    } catch {
      /* ignore */
    }
    try {
      if (
        typeof import.meta !== "undefined" &&
        import.meta.env &&
        import.meta.env.MODE !== "production"
      ) {
        return true;
      }
    } catch {
      /* ignore */
    }
    return false;
  })();

  useEffect(() => {
    if (!isValueDevDiagnosticsEnabled) return;
    if (publicRawReadyVisible.length > 0 || publicSourceCheckVisible.length > 0) return;
    try {
      console.debug("[ValueBetsPage empty diagnostics]", valueEmptyDiagnostics);
    } catch {
      /* ignore */
    }
  }, [
    isValueDevDiagnosticsEnabled,
    publicRawReadyVisible.length,
    publicSourceCheckVisible.length,
    valueEmptyDiagnostics,
  ]);

  const sportIdsMap = useMemo(() => {
    const map = {
      football: new Set(),
      tennis: new Set(),
      basketball: new Set(),
      esports: new Set(),
    };

    const passesBaseNonSport = (b) => {
      const live = isLiveMatch(b);
      const selectedEvMinRaw = Number(evFilter);
      const selectedEvMin = Number.isFinite(selectedEvMinRaw) ? selectedEvMinRaw : 1;
      if (b.ev < selectedEvMin) return false;
      if (mode === "live" && !live) return false;
      if (mode === "prematch" && live) return false;
      const hk = valueBetOfferedOddsToHkNumber(b);
      if (hk != null && !Number.isFinite(hk)) return false;
      return true;
    };

    bets.forEach((b) => {
      if (!passesBaseNonSport(b)) return;
      const sk = getValueRenderIdentityKey(b);
      if (!isPublicRawReadyRow(b) && !isPublicSourceCheckRow(b)) return;
      const key = getBetSport(b);
      if (map[key]) map[key].add(sk || b.id);
    });

    return map;
  }, [bets, mode, evFilter]);

  useEffect(() => {
    const shouldLog =
      (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") ||
      (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.MODE !== "production");
    if (!shouldLog) return;
    const selectedEvMinRaw = Number(evFilter);
    const selectedEvMin = Number.isFinite(selectedEvMinRaw) ? selectedEvMinRaw : 1;
    let afterMode = 0;
    let afterSport = 0;
    let afterEv = 0;
    let afterOddsDisplay = 0;
    let unknownSport = 0;
    let belowRecommendedEv = 0;
    let outsideLegacyHkDisplayBand = 0;
    for (let i = 0; i < bets.length; i += 1) {
      const b = bets[i];
      const live = isLiveMatch(b);
      if (mode === "live" && !live) continue;
      if (mode === "prematch" && live) continue;
      afterMode += 1;
      const betSport = getBetSport(b);
      if (!["football", "tennis", "basketball", "esports"].includes(betSport)) unknownSport += 1;
      if (betSport !== sport) continue;
      afterSport += 1;
      if (b.ev < getValueMinEvThresholdForBet(b)) belowRecommendedEv += 1;
      if (b.ev < selectedEvMin) continue;
      afterEv += 1;
      const hk = valueBetOfferedOddsToHkNumber(b);
      if (hk != null && Number.isFinite(hk) && (hk < VALUE_MIN_VISIBLE_HK_ODDS || hk > VALUE_MAX_VISIBLE_HK_ODDS)) {
        outsideLegacyHkDisplayBand += 1;
      }
      if (hk != null && !Number.isFinite(hk)) continue;
      afterOddsDisplay += 1;
    }
    console.debug("[ValueBetsPage] filter", {
      totalReceived: bets.length,
      afterMode,
      afterSport,
      afterEv,
      afterOddsDisplay,
      selectedMode: mode,
      selectedSport: sport,
      evFilter,
      unknownSport,
      belowRecommendedEv,
      outsideLegacyHkDisplayBand,
    });
  }, [bets, mode, sport, evFilter]);

  useEffect(() => {
    const shouldLog =
      (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") ||
      (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.MODE !== "production");
    if (!shouldLog) return;
    try {
      console.debug(
        "[Value translation audit]",
        visibleFiltered.slice(0, 5).map((row) => {
          // Mirror the same priority logic used inside renderValueBetCard so
          // the audit reflects what the user actually sees.
          const chainMarket =
            buildValueBetMarketFamilyOnlyRowLabel(row, lang) ||
            buildValueBetMarketRowLabel(row, lang) ||
            "";
          const semanticMarket = buildValueSemanticMarketDisplay(row, lang);
          let finalMarketLabel = chainMarket;
          if (!finalMarketLabel || looksLikeRawMarketKey(finalMarketLabel)) {
            if (semanticMarket && !looksLikeRawMarketKey(semanticMarket)) {
              finalMarketLabel = semanticMarket;
            } else if (finalMarketLabel && looksLikeRawMarketKey(finalMarketLabel)) {
              finalMarketLabel = humanizeRawMarketLabel(finalMarketLabel);
            }
          }

          const chainSelection = buildValueBetSelectionRowLabel(row, lang) || "";
          const semanticSelection = buildValueSemanticSelectionDisplay(row, lang);
          let finalSelectionLabel = chainSelection;
          if (!finalSelectionLabel || looksLikeRawMarketKey(finalSelectionLabel)) {
            if (semanticSelection && !looksLikeRawMarketKey(semanticSelection)) {
              finalSelectionLabel = semanticSelection;
            }
          }

          return {
            market_type: row?.market_type || row?.marketType,
            market: row?.market,
            selection: row?.selection,
            selection_type: row?.selection_type || row?.selectionType,
            line: row?.line,
            finalMarketLabel,
            finalSelectionLabel,
          };
        })
      );
    } catch {
      /* ignore */
    }
  }, [visibleFiltered, lang]);

  // Generic Value display audit (Task H): emits structured info about the
  // first few visible rows so we can verify market / selection / line / odds
  // come from the expected source (matched_raw vs opportunity_source) and
  // that the family rules in Task B were applied.
  useEffect(() => {
    const shouldLog =
      (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") ||
      (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.MODE !== "production");
    if (!shouldLog) return;
    try {
      console.debug(
        "[Value display audit]",
        visibleFiltered.slice(0, 5).map((row) => {
          const canonicalDisplay = buildValueCanonicalDisplayEx(row, lang);
          const verifiedRaw = getValueRowVerifiedRaw(row);
          const family = getValueRowMarketFamily(row, verifiedRaw);
          const isTeamTotalLike = String(canonicalDisplay?.marketFamily || family || "").trim() === "team_total";
          const teamTotalResolved = canonicalDisplay?.teamTotalResolved || resolveValueTeamTotalDisplaySideEx(row, verifiedRaw);
          const displayedMarket = String(canonicalDisplay?.marketLabel || "").trim() || buildValueDisplayMarketLabelEx(row, lang, verifiedRaw);
          const canonicalSelectionLabel = String(canonicalDisplay?.selectionLabel || "").trim();
          const teamTotalSafety = getValueTeamTotalSideSafety(row, displayedMarket);
          const teamTotalDisplayResolution = isTeamTotalLike ? resolveValueTeamTotalDisplaySideEx(row, verifiedRaw) : null;
          const teamTotalDisplayResolvedOk = Boolean(
            isTeamTotalLike &&
              teamTotalDisplayResolution &&
              teamTotalDisplayResolution.ok === true &&
              String(teamTotalDisplayResolution.teamName || "").trim()
          );
          const resolvedTeamName = String(teamTotalDisplayResolution?.teamName || "").trim();
          const canonicalSelectionContainsResolvedTeam =
            Boolean(canonicalSelectionLabel) &&
            Boolean(resolvedTeamName) &&
            canonicalSelectionLabel.toLowerCase().includes(resolvedTeamName.toLowerCase());
          const safeTeamTotalSelectionLabel = isTeamTotalLike
            ? buildSafeTeamTotalSelectionLabel(row, lang, teamTotalSafety, "display")
            : "";
          const isVi = String(lang || "").toLowerCase().startsWith("vi");
          const direction = String(teamTotalDisplayResolution?.direction || "").toUpperCase();
          const directionLabel =
            direction === "UNDER" ? (isVi ? "Xỉu" : "Under") : direction === "OVER" ? (isVi ? "Tài" : "Over") : "";
          const lineForDisplay = String(
            teamTotalDisplayResolution?.line ||
              formatValueRowDisplayLine(row, verifiedRaw) ||
              buildValueDisplayLineText(row) ||
              pickLineFromValueBet(row) ||
              ""
          ).trim();
          const displayResolvedLabel = [resolvedTeamName, directionLabel, lineForDisplay].filter(Boolean).join(" ").trim();
          const displayedSelectionBase = isTeamTotalLike
            ? canonicalSelectionContainsResolvedTeam
              ? canonicalSelectionLabel
              : teamTotalDisplayResolvedOk
                ? displayResolvedLabel || safeTeamTotalSelectionLabel || (isVi ? "Cần kiểm đội" : "Check team")
                : safeTeamTotalSelectionLabel || (isVi ? "Cần kiểm đội" : "Check team")
            : canonicalSelectionLabel || buildValueDisplaySelectionLabelEx(row, verifiedRaw, lang);
          const isPlayerPropDisplay = isValuePlayerPropLikeRow(row);
          const playerPropLine = formatValueRowDisplayLine(row, verifiedRaw) || pickLineFromValueBet(row);
          const selectionLabelBeforePlayerPropLinePatch = String(displayedSelectionBase || "").trim();
          let selectionLabelAfterPlayerPropLinePatch = selectionLabelBeforePlayerPropLinePatch;
          if (
            isPlayerPropDisplay &&
            isMeaningfulLineForCard(playerPropLine) &&
            selectionLabelAfterPlayerPropLinePatch &&
            !selectionAlreadyContainsLine(selectionLabelAfterPlayerPropLinePatch, playerPropLine) &&
            isDirectionLikeSelectionLabel(selectionLabelAfterPlayerPropLinePatch)
          ) {
            selectionLabelAfterPlayerPropLinePatch = `${selectionLabelAfterPlayerPropLinePatch} ${playerPropLine}`.trim();
          }
          const teamTotalNeedsSideWarning = isTeamTotalLike && !teamTotalDisplayResolvedOk;
          const displaySourcePriority = String(canonicalDisplay?.sourcePriority || "").trim() || (verifiedRaw ? "matched_raw" : "opportunity_source");
          const semanticDisplayKey = String(
            row.displaySemanticKey || getSemanticValueOpportunityDisplayKey(row) || getValueRenderIdentityKey(row) || ""
          ).trim();
          const duplicateBooks = Array.isArray(row._valueDuplicateBooks) ? row._valueDuplicateBooks : [];
          const duplicateIds = Array.isArray(row._valueDuplicateIds) ? row._valueDuplicateIds : [];
          const duplicateCountRaw = Number(row._valueDuplicateCount);
          const duplicateCount =
            Number.isFinite(duplicateCountRaw) && duplicateCountRaw > 0
              ? duplicateCountRaw
              : Math.max(1, duplicateIds.length);
          const chosenBook = String(row.sportsbook ?? row.bookmaker ?? row.book ?? "").trim();
          const chosenOddsDecimal = valueBetOfferedOddsToDecimal(row);
          const chosenTrueOdds = row.trueOdds || row.no_vig_odds || row.noVigOdds || null;
          const chosenEv =
            typeof row.ev === "number" && Number.isFinite(row.ev) ? row.ev : null;
          const chosenRawAgeSeconds =
            row?.offerVerification &&
            typeof row.offerVerification.rawAgeSeconds === "number" &&
            Number.isFinite(row.offerVerification.rawAgeSeconds)
              ? row.offerVerification.rawAgeSeconds
              : null;
          const chosenSourceAgeSeconds =
            row?.offerVerification &&
            typeof row.offerVerification.sourceAgeSeconds === "number" &&
            Number.isFinite(row.offerVerification.sourceAgeSeconds)
              ? row.offerVerification.sourceAgeSeconds
              : null;
          const chosenUpdatedAt =
            row.updatedAt || row.detected_at || row.detectedAt || row.timestamp || row.ts || null;
          const chosenOfferVerificationStatus = row.offerVerification?.status;
          return {
            oldRawEventIdentity: getValueSemanticEventIdentityToken(row),
            displayEventToken: getValueSemanticDisplayEventToken(row),
            semanticDisplayKey,
            duplicateCount,
            duplicateBooks,
            duplicateIds,
            chosenBook,
            chosenOddsDecimal,
            chosenTrueOdds,
            chosenEv,
            chosenRawAgeSeconds,
            chosenSourceAgeSeconds,
            chosenUpdatedAt,
            chosenOfferVerificationStatus,
            event_id: row.event_id || row.eventId || "",
            game_id: row.game_id || row.gameId || "",
            external_event_id: row.external_event_id || row.externalEventId || "",
            match: row.match || row.game || row.event_name || "",
            market_type: row.market_type || row.marketType || "",
            market: row.market || "",
            selection: row.selection || "",
            selection_type: row.selection_type || row.selectionType || "",
            line: row.line ?? row.point ?? row.handicap ?? row.total ?? "",
            sport: row.sport || "",
            league: row.league || "",
            rawMarketKey: canonicalDisplay?.rawMarketKey || "",
            sourcePriority: canonicalDisplay?.sourcePriority || displaySourcePriority,
            periodScope: canonicalDisplay?.periodScope || "",
            marketLabel: displayedMarket,
            selectionLabel: selectionLabelAfterPlayerPropLinePatch,
            isPlayerPropDisplay,
            playerPropLine,
            selectionLabelBeforePlayerPropLinePatch,
            selectionLabelAfterPlayerPropLinePatch,
            canonicalSelectionLabel,
            displaySourcePriority,
            isTeamTotalLike,
            teamTotalDisplayResolvedOk,
            teamTotalDisplayResolvedTeamName: teamTotalDisplayResolution?.teamName || "",
            teamTotalDisplayResolvedSide: teamTotalDisplayResolution?.side || "",
            teamTotalDisplayResolvedDirection: teamTotalDisplayResolution?.direction || "",
            teamTotalDisplayResolvedLine: teamTotalDisplayResolution?.line || "",
            teamTotalDisplayResolvedSource: teamTotalDisplayResolution?.source || "",
            teamTotalDisplayResolvedReason: teamTotalDisplayResolution?.reason || "",
            teamTotalNeedsSideWarning,
            teamTotalRawSideTrusted: Boolean(teamTotalSafety?.sideTrusted),
            teamTotalResolvedOk: isTeamTotalLike ? teamTotalResolved?.ok === true : null,
            teamTotalResolvedTeamName: isTeamTotalLike ? teamTotalResolved?.teamName || "" : null,
            teamTotalResolvedSide: isTeamTotalLike ? teamTotalResolved?.side || "" : null,
            teamTotalResolvedDirection: isTeamTotalLike ? teamTotalResolved?.direction || "" : null,
            teamTotalResolvedLine: isTeamTotalLike ? teamTotalResolved?.line || "" : null,
            teamTotalResolvedSource: isTeamTotalLike ? teamTotalResolved?.source || "" : null,
            teamTotalResolvedReason: isTeamTotalLike ? teamTotalResolved?.reason || "" : null,
            rowSelection: row.selection || "",
            rowDisplaySelection: row.display_selection || row.displaySelection || "",
            rawSelection: canonicalDisplay?.rawSelection || row.selection || "",
            rawSelectionType: canonicalDisplay?.rawSelectionType || row.selection_type || row.selectionType || "",
            rawLine: canonicalDisplay?.rawLine || row.line || "",
            rowLine: row.line,
            stat_category: row.stat_category || row.statCategory,
            is_player_prop: row.is_player_prop || row.isPlayerProp,
            home_team: row.home_team || row.homeTeam || "",
            away_team: row.away_team || row.awayTeam || "",
            offerVerificationStatus: row.offerVerification?.status,
          };
        })
      );
    } catch {
      /* ignore */
    }
  }, [visibleFiltered, lang]);

  useEffect(() => {
    setSeenBySport((prev) => {
      const next = {
        football: new Set(prev.football || []),
        tennis: new Set(prev.tennis || []),
        basketball: new Set(prev.basketball || []),
        esports: new Set(prev.esports || []),
      };

      const currentIds = sportIdsMap[sport] || new Set();
      currentIds.forEach((id) => next[sport].add(id));

      Object.keys(next).forEach((key) => {
        const liveIds = sportIdsMap[key] || new Set();
        next[key] = new Set([...next[key]].filter((id) => liveIds.has(id)));
      });

      return next;
    });
  }, [sport, sportIdsMap]);

  const getNewCountBySport = (sportKey) => {
    const liveIds = sportIdsMap[sportKey] || new Set();
    const seenIds = seenBySport[sportKey] || new Set();

    let count = 0;
    liveIds.forEach((id) => {
      if (!seenIds.has(id)) count += 1;
    });

    return count;
  };

  const modeTabs = [
    { key: "all", label: t.common.all },
    { key: "live", label: t.common.live },
    { key: "prematch", label: t.common.prematch },
  ];

  const sportTabs = [
    { key: "football", label: `⚽ ${t.sports.football}` },
    { key: "tennis", label: `🎾 ${t.sports.tennis}` },
    { key: "basketball", label: `🏀 ${t.sports.basketball}` },
    { key: "esports", label: `🎮 ${t.sports.esports}` },
  ];

  const activeModeIndex = modeTabs.findIndex((x) => x.key === mode);
  const activeUnderlineWidth = 100 / modeTabs.length;

  const handleCopyValueSlip = useCallback(async (stableKey, text) => {
    const payload = String(text || "").trim();
    if (!payload) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        throw new Error("clipboard_unavailable");
      }
    } catch {
      const el = document.createElement("textarea");
      el.value = payload;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(el);
      }
    }
    setCopiedKey(stableKey);
    setTimeout(() => {
      setCopiedKey((prev) => (prev === stableKey ? "" : prev));
    }, 1400);
  }, []);

  return (
    <div className="vb-container">
      <div className="vb-filter-bar">
        <div className="vb-segment-wrap">
          {modeTabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setMode(item.key)}
              className={`vb-segment-btn ${mode === item.key ? "is-active" : ""}`}
            >
              {item.label}
            </button>
          ))}

          <div
            className="vb-segment-underline"
            style={{
              width: `${activeUnderlineWidth}%`,
              left: `${activeUnderlineWidth * activeModeIndex}%`,
            }}
          />
        </div>

        <div ref={evDropdownRef} className="vb-select-wrap">
          <button
            type="button"
            onClick={() => setIsEvOpen((prev) => !prev)}
            className={`vb-select-button ${isEvOpen ? "is-open" : ""}`}
            aria-haspopup="listbox"
            aria-expanded={isEvOpen}
          >
            <span className="vb-select-button-text">≥{evFilter}%</span>
            <span
              className="vb-select-arrow"
              style={{
                transform: isEvOpen
                  ? "translateY(-50%) rotate(180deg)"
                  : "translateY(-50%) rotate(0deg)",
              }}
            >
              ▾
            </span>
          </button>

          {isEvOpen && (
            <div className="vb-dropdown-menu" role="listbox">
              {allowedEvOptions.map((value) => {
                const activeOption = evFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setEvFilter(value);
                      setIsEvOpen(false);
                    }}
                    className={`vb-dropdown-item ${activeOption ? "is-active" : ""}`}
                  >
                    ≥{value}%
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="vb-sport-scroll-wrap">
        <div className="vb-sport-scroll" role="tablist" aria-label={t.common.filter}>
          {sportTabs.map((item) => {
            const isActive = sport === item.key;
            const newCount = getNewCountBySport(item.key);

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setSport(item.key)}
                className={`vb-sport-btn ${isActive ? "is-active" : ""}`}
              >
                <span className="vb-sport-btn-text">{item.label}</span>
                {!isActive && newCount > 0 ? <span className="vb-sport-badge">+{newCount}</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="vb-list">
        {publicRawReadyVisible.length === 0 && (
          <div className="vb-no-data">
            {!hasReceivedValueBets
              ? t.common.loading
              : fetchError
                ? t.common.noData
                : "Chưa có kèo nào phù hợp. Đang chờ cơ hội đủ điều kiện."}
            {isValueDevDiagnosticsEnabled && (
              <div
                className="vb-empty-diagnostics"
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  lineHeight: 1.4,
                  opacity: 0.7,
                  textAlign: "left",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  [dev diagnostics — empty Value list]
                </div>
                <div>bets.length: {valueEmptyDiagnostics.betsLength}</div>
                <div>mode: {String(valueEmptyDiagnostics.selectedMode)}</div>
                <div>sport: {String(valueEmptyDiagnostics.selectedSport)}</div>
                <div>evFilter: {String(valueEmptyDiagnostics.selectedEvFilter)}</div>
                <div>after mode filter: {valueEmptyDiagnostics.afterModeFilter}</div>
                <div>after sport filter: {valueEmptyDiagnostics.afterSportFilter}</div>
                <div>after EV filter: {valueEmptyDiagnostics.afterEvFilter}</div>
                <div>rawReady candidates: {valueEmptyDiagnostics.rawReadyCandidates}</div>
                <div>
                  sourceVerified candidates: {valueEmptyDiagnostics.sourceVerifiedCandidates}
                </div>
                <div>
                  publicRawReadyVisible.length: {valueEmptyDiagnostics.publicRawReadyVisibleLength}
                </div>
                <div>
                  publicSourceCheckVisible.length:{" "}
                  {valueEmptyDiagnostics.publicSourceCheckVisibleLength}
                </div>
                <div style={{ marginTop: 4 }}>sample (first 5 rows):</div>
                {valueEmptyDiagnostics.sampleFirstFive.length === 0 ? (
                  <div>(no rows received)</div>
                ) : (
                  valueEmptyDiagnostics.sampleFirstFive.map((row, idx) => (
                    <div key={`vb-empty-diag-${idx}`} style={{ marginTop: 2 }}>
                      #{idx + 1} sport={String(row.sport)} | league={String(row.league)} | ev=
                      {String(row.ev)} | ov.status={String(row.offerVerificationStatus)} |
                      readiness={String(row.betReadiness)} | lifecycle=
                      {String(row.lifecycleStatus)} | getBetSport={String(row.getBetSport)} |
                      isLiveMatch={String(row.isLiveMatch)}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {publicRawReadyVisible.map((bet) => {
          const rk = getValueRenderIdentityKey(bet);
          const isLeaving = leavingKeys.has(rk);
          const homeLow = String(bet?.home_team ?? bet?.homeTeam ?? "").trim().toLowerCase();
          const awayLow = String(bet?.away_team ?? bet?.awayTeam ?? "").trim().toLowerCase();
          const mktRaw = String(
            bet?.market_type ?? bet?.marketType ?? bet?.market ?? ""
          ).trim();
          const selRaw = String(
            bet?.selection ?? bet?.display_selection ?? bet?.displaySelection
              ?? bet?.pick ?? bet?.outcome ?? ""
          ).trim();
          const mktNorm = normalizeSplitsMarketKey(mktRaw);
          const selNorm = normalizeSplitsSelectionKey(selRaw);
          const eid = String(
            bet?.event_id ?? bet?.eventId ?? bet?.game_id ?? ""
          ).trim();
          const splitData = (() => {
            if (!mktNorm || !selNorm) return null;
            // Try team-name key first (most reliable)
            if (homeLow && awayLow) {
              const byTeam = splitsMap[`${homeLow}|${awayLow}||${mktNorm}||${selNorm}`];
              if (byTeam) return byTeam;
            }
            // Try eventId key second
            if (eid) {
              const byEid = splitsMap[`${eid}||${mktNorm}||${selNorm}`];
              if (byEid) return byEid;
            }
            return null;
          })();
          const gsKey = buildGameStateKey(
            bet?.home_team ?? bet?.homeTeam ?? "",
            bet?.away_team ?? bet?.awayTeam ?? ""
          );
          const gameState = gsKey ? gameStateMap[gsKey] ?? null : null;
          const gameStateExtras = gameState
            ? extractGameStateExtras(gameState)
            : null;
          const gameStatePeriodLabel = gameState
            ? formatGameStatePeriodLabel(gameState)
            : "";
          return (
            <ValueCard
              key={rk}
              bet={bet}
              lang={lang}
              t={t}
              timeZone={timeZone}
              isFlashing={flashKeys.has(rk)}
              isLeaving={isLeaving}
              splits={splitData}
              isCopied={copiedKey === rk}
              onCopy={handleCopyValueSlip}
              isDebugWatchlist={false}
              gameState={gameState}
              gameStateExtras={gameStateExtras}
              gameStatePeriodLabel={gameStatePeriodLabel}
              oddsFormat={oddsFormat}
            />
          );
        })}

        {publicSourceCheckVisible.map((bet) => {
          const rk = getValueRenderIdentityKey(bet);
          const isLeaving = leavingKeys.has(rk);
          const homeLow = String(bet?.home_team ?? bet?.homeTeam ?? "").trim().toLowerCase();
          const awayLow = String(bet?.away_team ?? bet?.awayTeam ?? "").trim().toLowerCase();
          const mktRaw = String(
            bet?.market_type ?? bet?.marketType ?? bet?.market ?? ""
          ).trim();
          const selRaw = String(
            bet?.selection ?? bet?.display_selection ?? bet?.displaySelection
              ?? bet?.pick ?? bet?.outcome ?? ""
          ).trim();
          const mktNorm = normalizeSplitsMarketKey(mktRaw);
          const selNorm = normalizeSplitsSelectionKey(selRaw);
          const eid = String(
            bet?.event_id ?? bet?.eventId ?? bet?.game_id ?? ""
          ).trim();
          const splitData = (() => {
            if (!mktNorm || !selNorm) return null;
            // Try team-name key first (most reliable)
            if (homeLow && awayLow) {
              const byTeam = splitsMap[`${homeLow}|${awayLow}||${mktNorm}||${selNorm}`];
              if (byTeam) return byTeam;
            }
            // Try eventId key second
            if (eid) {
              const byEid = splitsMap[`${eid}||${mktNorm}||${selNorm}`];
              if (byEid) return byEid;
            }
            return null;
          })();
          const gsKey = buildGameStateKey(
            bet?.home_team ?? bet?.homeTeam ?? "",
            bet?.away_team ?? bet?.awayTeam ?? ""
          );
          const gameState = gsKey ? gameStateMap[gsKey] ?? null : null;
          const gameStateExtras = gameState
            ? extractGameStateExtras(gameState)
            : null;
          const gameStatePeriodLabel = gameState
            ? formatGameStatePeriodLabel(gameState)
            : "";
          return (
            <ValueCard
              key={rk}
              bet={bet}
              lang={lang}
              t={t}
              timeZone={timeZone}
              isFlashing={flashKeys.has(rk)}
              isLeaving={isLeaving}
              splits={splitData}
              isCopied={copiedKey === rk}
              onCopy={handleCopyValueSlip}
              isDebugWatchlist={false}
              gameState={gameState}
              gameStateExtras={gameStateExtras}
              gameStatePeriodLabel={gameStatePeriodLabel}
              oddsFormat={oddsFormat}
            />
          );
        })}
      </div>
    </div>
  );
}
